import {
  createWorker,
  PSM,
  type TesseractWorker,
} from "tesseract.js";

import { isValidStorageAddress, normalizeStorageAddress } from "../utils/storageAddress";
import { recordParallelRuntimeLifecycle } from "./parallelRuntimeSoakService";
import {
  createVisionRuntimeSnapshot,
  type VisionRuntimeProvenance,
  type VisualLabOcrRegion,
} from "./visualLabService";
import {
  VECTOR_TEMPLATE_HORIZONTAL_LINES,
  VECTOR_TEMPLATE_OCR_REGIONS,
  VECTOR_TEMPLATE_STORAGE_SUBCELL_ROIS,
  VECTOR_TEMPLATE_VERTICAL_LINES,
} from "../generated/vectorTemplateContract";

import type {
  RecognizedLabelData,
  ScanOcrDetails,
  ScanOcrFieldResult,
  ScanOcrGridAnchoringDetails,
  ScanOcrParallelShadowTask,
  ScanOcrPassResult,
  ScanStorageAddressDecoderDetails,
  ScanStorageAddressSubcellName,
  ScanStorageAddressSubcellResult,
} from "../types/scanWorkflow";

const OCR_LANGUAGES = "deu+eng";
const TESSERACT_WORKER_PATH = "/tesseract/worker.min.js";
const TESSERACT_CORE_PATH = "/tesseract/core";
const TESSERACT_LANG_PATH = "/tesseract/lang";
const IOS_TRAINEDDATA_ASSETS = [
  {
    code: "deu",
    url: `${TESSERACT_LANG_PATH}/deu.traineddata.bin`,
    sha256: "306c4280d0cbed46fbff727486bd43b92730181bae80f56941a091f363bdf28b",
  },
  {
    code: "eng",
    url: `${TESSERACT_LANG_PATH}/eng.traineddata.bin`,
    sha256: "45b4cb346724ac1774f1c36f42f182b887bcdb28ebe63e6fff90ac41f3fcff91",
  },
] as const;
// Tesseract can adapt its classifier/document dictionary across recognize() calls.
// Production scans are unrelated documents and must be replay-deterministic, so
// keep the worker warm for performance but disable cross-call learning state.
const TESSERACT_DETERMINISTIC_PARAMETERS = {
  classify_enable_learning: "0",
  tessedit_enable_doc_dict: "0",
} as const;

interface NormalizedOcrRegion {
  field: ScanOcrFieldResult["field"];
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  mode: "line" | "block";
  whitelist?: string;
}

// Referenzlayout: perspektivisch normiertes Etikett mit 1800 x 432 px.
// Die Werte sind bewusst normalisiert, damit die OCR auch mit einer anderen
// Ausgabeauflösung funktioniert. Grundlage ist das feste Kanban-Etikettenlayout.
const OCR_REGIONS: readonly NormalizedOcrRegion[] = [
  {
    field: "materialText",
    label: "Materialkurztext",
    ...VECTOR_TEMPLATE_OCR_REGIONS.materialText,
    mode: "block",
  },
  {
    field: "materialType",
    label: "Typ",
    ...VECTOR_TEMPLATE_OCR_REGIONS.materialType,
    mode: "line",
    whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789ÄÖÜäöüß-_/.,+() ",
  },
  {
    field: "materialNumber",
    label: "Materialnummer",
    ...VECTOR_TEMPLATE_OCR_REGIONS.materialNumber,
    mode: "line",
    whitelist: "0123456789",
  },
  {
    field: "storageAddress",
    label: "Lagerort",
    ...VECTOR_TEMPLATE_OCR_REGIONS.storageAddress,
    mode: "block",
    whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 ",
  },
];

// SR26 exposes the exact four storage-address cells from the vector source.
// The current Tesseract baseline still reads the enclosing block for latency/parity;
// SR27's engine/decoder interface can consume these immutable sub-ROIs without
// inventing geometry or re-deriving it from screenshots.
export const VECTOR_STORAGE_SUBCELL_REGIONS = VECTOR_TEMPLATE_STORAGE_SUBCELL_ROIS;

const STORAGE_ADDRESS_DECODER_CONTRACT = "storage-address-four-subcell-decoder-v1" as const;
const STORAGE_SUBCELL_ORDER: readonly ScanStorageAddressSubcellName[] = [
  "building",
  "cabinet",
  "compartment",
  "subCompartment",
] as const;
const STORAGE_SUBCELL_LABELS: Record<ScanStorageAddressSubcellName, ScanStorageAddressSubcellResult["label"]> = {
  building: "Gebäude",
  cabinet: "Schrank",
  compartment: "Fach",
  subCompartment: "Unterfach",
};
const STORAGE_SUBCELL_WHITELISTS: Record<ScanStorageAddressSubcellName, string> = {
  building: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
  cabinet: "0123456789",
  compartment: "0123456789",
  subCompartment: "0123456789",
};

let workerPromise: Promise<TesseractWorker> | null = null;
let workerReady = false;
let workerProgressListener: ((progress: number) => void) | null = null;
let workerTerminationPromise: Promise<void> | null = null;
let workerGeneration = 0;
let workerBootPhase = "cold";
let workerBootStartedAtUtc: string | null = null;
let workerBootStartedAtMs: number | null = null;
let workerBootFinishedAtUtc: string | null = null;
let workerBootDurationMs: number | null = null;
let workerLastLoggerStatus: string | null = null;
let workerLastError: string | null = null;
let workerLastVisibilityHiddenAtUtc: string | null = null;
let workerLastVisibilityVisibleAtUtc: string | null = null;
let workerLastPageHideAtUtc: string | null = null;
let workerLastPageShowAtUtc: string | null = null;

type OcrCoreMode = "automatic" | "simd-lstm" | "plain-lstm";
type OcrTraineddataTransport = "worker-fetch" | "main-thread-preloaded" | "main-thread-preload-failed";

type OcrLanguageAssetDiagnostic = {
  code: "deu" | "eng";
  url: string;
  expectedSha256: string;
  actualSha256: string | null;
  byteLength: number | null;
  httpStatus: number | null;
  gzipMagicValid: boolean | null;
  sha256Match: boolean | null;
  startedAtUtc: string;
  finishedAtUtc: string | null;
  durationMs: number | null;
  outcome: "loading" | "verified" | "failed";
  error: string | null;
};

type PreloadedOcrLanguageAsset = {
  code: "deu" | "eng";
  data: Uint8Array;
};

type OcrWorkerErrorDiagnostic = {
  message: string | null;
  filename: string | null;
  lineno: number | null;
  colno: number | null;
  errorName: string | null;
  errorMessage: string | null;
};

type OcrBootAttemptDiagnostic = {
  attempt: number;
  coreMode: OcrCoreMode;
  corePath: string;
  startedAtUtc: string;
  finishedAtUtc: string | null;
  durationMs: number | null;
  outcome: "starting" | "success" | "worker_error_event" | "failed";
  lastLoggerStatus: string | null;
  workerError: OcrWorkerErrorDiagnostic | null;
};

let workerSelectedCoreMode: OcrCoreMode = "automatic";
let workerSelectedCorePath = TESSERACT_CORE_PATH;
let workerAttemptNumber = 0;
let workerAttemptHistory: OcrBootAttemptDiagnostic[] = [];
let workerLastErrorEvent: OcrWorkerErrorDiagnostic | null = null;
let workerFallbackStarted = false;
let workerFallbackSucceeded = false;
let workerFallbackFailed = false;
let workerTraineddataTransport: OcrTraineddataTransport = "worker-fetch";
let workerLanguageAssetDiagnostics: OcrLanguageAssetDiagnostic[] = [];
let workerIosLanguageAssets: PreloadedOcrLanguageAsset[] | null = null;

export interface LabelOcrRuntimeDiagnostic {
  schemaVersion: 3;
  contractVersion: "label-ocr-runtime-diagnostic-v3-ios-main-thread-traineddata";
  state: "cold" | "starting" | "warm";
  generation: number;
  phase: string;
  workerPath: string;
  corePath: string;
  langPath: string;
  selectedCoreMode: OcrCoreMode;
  attemptNumber: number;
  attempts: OcrBootAttemptDiagnostic[];
  workerError: OcrWorkerErrorDiagnostic | null;
  fallbackStarted: boolean;
  fallbackSucceeded: boolean;
  fallbackFailed: boolean;
  traineddataTransport: OcrTraineddataTransport;
  languageAssets: OcrLanguageAssetDiagnostic[];
  startedAtUtc: string | null;
  finishedAtUtc: string | null;
  startupDurationMs: number | null;
  lastLoggerStatus: string | null;
  lastError: string | null;
  terminationPending: boolean;
  secureContext: boolean | null;
  serviceWorkerControlled: boolean | null;
  visibilityState: string | null;
  lastVisibilityHiddenAtUtc: string | null;
  lastVisibilityVisibleAtUtc: string | null;
  lastPageHideAtUtc: string | null;
  lastPageShowAtUtc: string | null;
}

function isIosWebKitOcrRuntime(): boolean {
  if (typeof navigator === "undefined") return false;
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /iPhone|iPad|iPod/i.test(userAgent)
    || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function readStringProperty(value: unknown, name: string): string | null {
  if (!value || typeof value !== "object") return null;
  const result = Reflect.get(value, name);
  return typeof result === "string" && result.trim() ? result : null;
}

function readNumberProperty(value: unknown, name: string): number | null {
  if (!value || typeof value !== "object") return null;
  const result = Reflect.get(value, name);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

function describeWorkerError(error: unknown): OcrWorkerErrorDiagnostic {
  const nested = error && typeof error === "object" ? Reflect.get(error, "error") : null;
  return {
    message: error instanceof Error ? error.message : readStringProperty(error, "message") ?? String(error),
    filename: readStringProperty(error, "filename"),
    lineno: readNumberProperty(error, "lineno"),
    colno: readNumberProperty(error, "colno"),
    errorName: nested instanceof Error ? nested.name : readStringProperty(nested, "name"),
    errorMessage: nested instanceof Error ? nested.message : readStringProperty(nested, "message"),
  };
}

export function getLabelOcrRuntimeState(): "cold" | "starting" | "warm" {
  if (workerReady && !workerTerminationPromise) return "warm";
  return workerPromise || workerTerminationPromise ? "starting" : "cold";
}

export function getLabelOcrRuntimeDiagnostic(): LabelOcrRuntimeDiagnostic {
  return {
    schemaVersion: 3,
    contractVersion: "label-ocr-runtime-diagnostic-v3-ios-main-thread-traineddata",
    state: getLabelOcrRuntimeState(),
    generation: workerGeneration,
    phase: workerBootPhase,
    workerPath: TESSERACT_WORKER_PATH,
    corePath: workerSelectedCorePath,
    langPath: TESSERACT_LANG_PATH,
    selectedCoreMode: workerSelectedCoreMode,
    attemptNumber: workerAttemptNumber,
    attempts: workerAttemptHistory.map((attempt) => ({
      ...attempt,
      workerError: attempt.workerError ? { ...attempt.workerError } : null,
    })),
    workerError: workerLastErrorEvent ? { ...workerLastErrorEvent } : null,
    fallbackStarted: workerFallbackStarted,
    fallbackSucceeded: workerFallbackSucceeded,
    fallbackFailed: workerFallbackFailed,
    traineddataTransport: workerTraineddataTransport,
    languageAssets: workerLanguageAssetDiagnostics.map((asset) => ({ ...asset })),
    startedAtUtc: workerBootStartedAtUtc,
    finishedAtUtc: workerBootFinishedAtUtc,
    startupDurationMs: workerBootDurationMs,
    lastLoggerStatus: workerLastLoggerStatus,
    lastError: workerLastError,
    terminationPending: workerTerminationPromise !== null,
    secureContext: typeof window !== "undefined" ? window.isSecureContext : null,
    serviceWorkerControlled: typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker.controller !== null
      : null,
    visibilityState: typeof document !== "undefined" ? document.visibilityState : null,
    lastVisibilityHiddenAtUtc: workerLastVisibilityHiddenAtUtc,
    lastVisibilityVisibleAtUtc: workerLastVisibilityVisibleAtUtc,
    lastPageHideAtUtc: workerLastPageHideAtUtc,
    lastPageShowAtUtc: workerLastPageShowAtUtc,
  };
}

export function recordLabelOcrRuntimeLifecycle(
  event: "visibility_hidden" | "visibility_visible" | "pagehide" | "pageshow",
): void {
  const now = new Date().toISOString();
  if (event === "visibility_hidden") workerLastVisibilityHiddenAtUtc = now;
  if (event === "visibility_visible") workerLastVisibilityVisibleAtUtc = now;
  if (event === "pagehide") workerLastPageHideAtUtc = now;
  if (event === "pageshow") workerLastPageShowAtUtc = now;
}

function formatOcrStartupError(error: unknown): Error {
  const detail = error instanceof Error ? error.message : String(error);
  const diagnostic = getLabelOcrRuntimeDiagnostic();
  const languageSummary = diagnostic.languageAssets.length > 0
    ? diagnostic.languageAssets.map((asset) =>
        `${asset.code}:${asset.outcome}:${asset.httpStatus ?? "no-status"}:${asset.byteLength ?? "no-bytes"}B:${asset.sha256Match === null ? "sha-unknown" : asset.sha256Match ? "sha-ok" : "sha-mismatch"}`
      ).join(",")
    : "none";
  const wrapped = new Error(
    `OCR_RUNTIME_INIT_FAILED · Generation ${diagnostic.generation} · Attempt ${diagnostic.attemptNumber}`
    + ` · Core ${diagnostic.selectedCoreMode} · Phase ${diagnostic.phase}`
    + ` · secureContext ${diagnostic.secureContext === null ? "unknown" : diagnostic.secureContext ? "yes" : "no"}`
    + ` · serviceWorker ${diagnostic.serviceWorkerControlled === null ? "unknown" : diagnostic.serviceWorkerControlled ? "controlled" : "uncontrolled"}`
    + ` · visibility ${diagnostic.visibilityState ?? "unknown"}`
    + ` · traineddata ${diagnostic.traineddataTransport} [${languageSummary}] · ${detail}`,
  );
  wrapped.name = "OcrRuntimeInitializationError";
  return wrapped;
}

// SR17/v0.282.7: rein lokaler OCR-Start mit strikt genau einem produktiven
// Tesseract-Boot zur Zeit. v0.286.1 erweitert denselben Single-Flight-Besitzer
// auf eine serielle iOS-Kompatibilitaetskette: automatic -> simd-lstm -> plain-lstm.
// Ein Fallback startet ausschliesslich nach einem echten Worker-ErrorEvent.
export async function warmLabelOcrRuntime(): Promise<void> {
  await getWorker();
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function createOcrBootPlan(): Array<{ mode: OcrCoreMode; corePath: string }> {
  const automatic = { mode: "automatic" as const, corePath: TESSERACT_CORE_PATH };
  if (!isIosWebKitOcrRuntime()) return [automatic];
  return [
    automatic,
    { mode: "simd-lstm", corePath: `${TESSERACT_CORE_PATH}/tesseract-core-simd-lstm.wasm.js` },
    { mode: "plain-lstm", corePath: `${TESSERACT_CORE_PATH}/tesseract-core-lstm.wasm.js` },
  ];
}

type ManagedBrowserWorkerPacket = {
  workerId: string;
  jobId: string;
  action: string;
  status?: "resolve" | "reject" | "progress";
  data?: unknown;
};

type ManagedBrowserWorkerPending = {
  resolve: (value: { jobId: string; data: unknown }) => void;
  reject: (reason: unknown) => void;
};

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("WebCrypto SHA-256 ist für die OCR-Traineddata-Prüfung nicht verfügbar.");
  }
  const digestInput = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(digestInput).set(bytes);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", digestInput);
  return bytesToHex(new Uint8Array(digest));
}

async function preloadIosLanguageAssets(): Promise<PreloadedOcrLanguageAsset[]> {
  const loaded: PreloadedOcrLanguageAsset[] = [];
  workerLanguageAssetDiagnostics = [];
  workerTraineddataTransport = "main-thread-preloaded";
  workerBootPhase = "preloading language traineddata main-thread";
  workerLastLoggerStatus = "preloading language traineddata main-thread";

  for (const asset of IOS_TRAINEDDATA_ASSETS) {
    const startedAtMs = performance.now();
    const diagnostic: OcrLanguageAssetDiagnostic = {
      code: asset.code,
      url: asset.url,
      expectedSha256: asset.sha256,
      actualSha256: null,
      byteLength: null,
      httpStatus: null,
      gzipMagicValid: null,
      sha256Match: null,
      startedAtUtc: new Date().toISOString(),
      finishedAtUtc: null,
      durationMs: null,
      outcome: "loading",
      error: null,
    };
    workerLanguageAssetDiagnostics.push(diagnostic);

    try {
      const response = await fetch(asset.url, {
        cache: "default",
        credentials: "same-origin",
      });
      diagnostic.httpStatus = response.status;
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} beim Laden von ${asset.url}.`);
      }

      const data = new Uint8Array(await response.arrayBuffer());
      diagnostic.byteLength = data.byteLength;
      diagnostic.gzipMagicValid = data.byteLength >= 2 && data[0] === 0x1f && data[1] === 0x8b;
      if (!diagnostic.gzipMagicValid) {
        throw new Error(`Ungültige GZIP-Signatur für ${asset.url}.`);
      }

      const actualSha256 = await sha256Hex(data);
      diagnostic.actualSha256 = actualSha256;
      diagnostic.sha256Match = actualSha256 === asset.sha256;
      if (!diagnostic.sha256Match) {
        throw new Error(
          `SHA-256 stimmt für ${asset.url} nicht überein (erwartet ${asset.sha256}, erhalten ${actualSha256}).`
        );
      }

      diagnostic.outcome = "verified";
      loaded.push({ code: asset.code, data });
    } catch (error: unknown) {
      diagnostic.outcome = "failed";
      diagnostic.error = error instanceof Error ? error.message : String(error);
      workerTraineddataTransport = "main-thread-preload-failed";
      workerBootPhase = "language_asset_main_thread_failed";
      workerLastLoggerStatus = "loading language traineddata main-thread failed";
      workerLastError = diagnostic.error;
      throw error;
    } finally {
      diagnostic.finishedAtUtc = new Date().toISOString();
      diagnostic.durationMs = Math.max(0, performance.now() - startedAtMs);
    }
  }

  workerBootPhase = "language_asset_main_thread_verified";
  workerLastLoggerStatus = "language traineddata main-thread verified";
  return loaded;
}

async function serializeManagedWorkerImage(image: unknown): Promise<Uint8Array> {
  const readBlob = (blob: Blob): Promise<Uint8Array> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error("OCR-Bild konnte nicht gelesen werden."));
    reader.readAsArrayBuffer(blob);
  });

  if (image instanceof Blob) return readBlob(image);
  if (image instanceof HTMLCanvasElement) {
    const blob = await new Promise<Blob>((resolve, reject) => {
      image.toBlob((value) => {
        if (value) resolve(value);
        else reject(new Error("OCR-Canvas konnte nicht serialisiert werden."));
      });
    });
    return readBlob(blob);
  }
  if (image instanceof Uint8Array) return image;
  if (image instanceof ArrayBuffer) return new Uint8Array(image);
  throw new Error("Nicht unterstütztes OCR-Bildformat für den iOS-Kompatibilitätsworker.");
}

type ManagedIosTesseractWorkerOptions = {
  workerId?: string;
  languageAssets?: PreloadedOcrLanguageAsset[];
  updatePrimaryDiagnostics?: boolean;
};

async function createManagedIosTesseractWorker(
  corePath: string,
  attemptDiagnostic: OcrBootAttemptDiagnostic,
  options: ManagedIosTesseractWorkerOptions = {},
): Promise<TesseractWorker> {
  const rawWorker = new Worker(TESSERACT_WORKER_PATH);
  const workerId = options.workerId ?? `ManagedIOSWorker-${workerGeneration}-${attemptDiagnostic.attempt}`;
  const updatePrimaryDiagnostics = options.updatePrimaryDiagnostics ?? true;
  const pending = new Map<string, ManagedBrowserWorkerPending>();
  let nextJobId = 0;
  let terminated = false;

  const terminateRawWorker = (): void => {
    if (terminated) return;
    terminated = true;
    rawWorker.terminate();
    for (const entry of pending.values()) {
      entry.reject(new Error("OCR-Worker wurde beendet."));
    }
    pending.clear();
  };

  const sendJob = (
    action: string,
    payload: Record<string, unknown>,
  ): Promise<{ jobId: string; data: unknown }> => {
    if (terminated) return Promise.reject(new Error("OCR-Worker ist bereits beendet."));
    const jobId = `ManagedJob-${workerGeneration}-${attemptDiagnostic.attempt}-${nextJobId++}`;
    return new Promise((resolve, reject) => {
      pending.set(`${action}-${jobId}`, { resolve, reject });
      rawWorker.postMessage({ workerId, jobId, action, payload });
    });
  };

  rawWorker.onmessage = (event: MessageEvent<ManagedBrowserWorkerPacket>) => {
    const packet = event.data;
    const key = `${packet.action}-${packet.jobId}`;
    if (packet.status === "progress") {
      const progress = packet.data as { status?: unknown; progress?: unknown } | undefined;
      const status = typeof progress?.status === "string" && progress.status.trim()
        ? progress.status.trim()
        : null;
      if (status) {
        attemptDiagnostic.lastLoggerStatus = status;
        if (updatePrimaryDiagnostics) {
          workerLastLoggerStatus = status;
          workerBootPhase = status;
        }
      }
      if (
        updatePrimaryDiagnostics
        && typeof progress?.progress === "number"
        && Number.isFinite(progress.progress)
      ) {
        workerProgressListener?.(clamp(progress.progress, 0, 1));
      }
      return;
    }

    const entry = pending.get(key);
    if (!entry) return;
    pending.delete(key);
    if (packet.status === "resolve") {
      entry.resolve({ jobId: packet.jobId, data: packet.data });
    } else {
      const message = typeof packet.data === "string"
        ? packet.data
        : `Tesseract-Job ${packet.action} fehlgeschlagen.`;
      const detail: OcrWorkerErrorDiagnostic = {
        message,
        filename: null,
        lineno: null,
        colno: null,
        errorName: null,
        errorMessage: message,
      };
      if (updatePrimaryDiagnostics) {
        workerLastErrorEvent = detail;
        workerLastError = message;
        workerBootPhase = "worker_error_event";
      }
      attemptDiagnostic.outcome = "worker_error_event";
      attemptDiagnostic.workerError = detail;
      entry.reject(new Error(message));
    }
  };

  rawWorker.onerror = (event: ErrorEvent) => {
    const detail = describeWorkerError(event);
    const errorMessage = detail.message ?? detail.errorMessage ?? "Tesseract worker error event";
    if (updatePrimaryDiagnostics) {
      workerLastErrorEvent = detail;
      workerLastError = errorMessage;
      workerBootPhase = "worker_error_event";
    }
    attemptDiagnostic.outcome = "worker_error_event";
    attemptDiagnostic.workerError = detail;
    for (const entry of pending.values()) entry.reject(event.error ?? new Error(errorMessage));
    pending.clear();
    terminateRawWorker();
  };

  try {
    await sendJob("load", {
      options: { lstmOnly: true, corePath, logging: false },
    });
    const preloadedLanguages = options.languageAssets ?? workerIosLanguageAssets;
    if (!preloadedLanguages || preloadedLanguages.length !== IOS_TRAINEDDATA_ASSETS.length) {
      throw new Error("Verifizierte iOS-OCR-Sprachdaten sind vor dem Worker-Start nicht verfügbar.");
    }
    await sendJob("loadLanguage", {
      // Tesseract.js v7 loadLanguage accepts {code,data} objects and therefore
      // skips its WebWorker fetch(). initialize still receives the language-code
      // string because v7.0.0 maps Lang objects incorrectly during initialize.
      langs: preloadedLanguages.map((language) => ({
        code: language.code,
        data: language.data,
      })),
      options: {
        langPath: TESSERACT_LANG_PATH,
        cacheMethod: "none",
        gzip: true,
        lstmOnly: true,
      },
    });
    await sendJob("initialize", {
      langs: OCR_LANGUAGES,
      oem: 1,
      config: {},
    });
  } catch (error: unknown) {
    terminateRawWorker();
    throw error;
  }

  const managedWorker = {
    setParameters: async (params: Record<string, string | number>) => sendJob("setParameters", { params }),
    recognize: async (image: unknown, options: Record<string, unknown> = {}, output = { text: true }) => {
      const serialized = await serializeManagedWorkerImage(image);
      return sendJob("recognize", { image: serialized, options, output });
    },
    terminate: async () => {
      terminateRawWorker();
    },
  };

  return managedWorker as unknown as TesseractWorker;
}

async function bootWorkerSequence(): Promise<TesseractWorker> {
  const plan = createOcrBootPlan();
  let lastFailure: unknown = new Error("OCR-Worker konnte nicht initialisiert werden.");

  if (isIosWebKitOcrRuntime()) {
    try {
      // v0.286.4: Safari/WebKit kann den traineddata-fetch aus dem Tesseract-
      // WebWorker mit TypeError: Load failed abbrechen. Die exakt gepinnten
      // lokalen .traineddata.gz-Assets werden deshalb einmal im Main Thread
      // geladen, GZIP/SHA-verifiziert und anschließend als Bytes in jeden
      // seriellen Core-Attempt gereicht. Ein Asset-Fehler ist kein Core-Fehler
      // und startet deshalb bewusst keinen automatic->SIMD->Plain-Retry.
      workerIosLanguageAssets = await preloadIosLanguageAssets();
    } catch (error: unknown) {
      workerReady = false;
      workerBootFinishedAtUtc = new Date().toISOString();
      workerBootDurationMs = workerBootStartedAtMs === null
        ? null
        : Math.max(0, performance.now() - workerBootStartedAtMs);
      throw formatOcrStartupError(error);
    }
  }

  for (let index = 0; index < plan.length; index += 1) {
    const entry = plan[index];
    const attempt = index + 1;
    const attemptStartedMs = performance.now();
    const attemptDiagnostic: OcrBootAttemptDiagnostic = {
      attempt,
      coreMode: entry.mode,
      corePath: entry.corePath,
      startedAtUtc: new Date().toISOString(),
      finishedAtUtc: null,
      durationMs: null,
      outcome: "starting",
      lastLoggerStatus: null,
      workerError: null,
    };
    workerAttemptHistory.push(attemptDiagnostic);
    workerAttemptNumber = attempt;
    workerSelectedCoreMode = entry.mode;
    workerSelectedCorePath = entry.corePath;
    if (attempt > 1) workerFallbackStarted = true;
    workerBootPhase = "worker_create_requested";

    let errorEventSeen = false;
    let created: Promise<TesseractWorker>;
    try {
      if (isIosWebKitOcrRuntime()) {
        // v0.286.2: Tesseract.js 7 kann createWorker() nach einem reject aus
        // loadLanguage/initialize offen lassen. Der iOS-Pfad besitzt deshalb den
        // Browser-Worker selbst und kann ihn bei jedem Fehler wirklich terminieren,
        // bevor die nächste Core-Variante seriell startet.
        created = createManagedIosTesseractWorker(entry.corePath, attemptDiagnostic);
      } else {
        created = createWorker(
          OCR_LANGUAGES,
          undefined,
          {
            workerPath: TESSERACT_WORKER_PATH,
            corePath: entry.corePath,
            langPath: TESSERACT_LANG_PATH,
            workerBlobURL: false,
            errorHandler: (error) => {
              errorEventSeen = true;
              const detail = describeWorkerError(error);
              workerLastErrorEvent = detail;
              workerLastError = detail.message;
              workerBootPhase = "worker_error_event";
              attemptDiagnostic.outcome = "worker_error_event";
              attemptDiagnostic.workerError = detail;
            },
            logger: (message) => {
              const status = typeof message.status === "string" && message.status.trim()
                ? message.status.trim()
                : null;
              if (status) {
                workerLastLoggerStatus = status;
                attemptDiagnostic.lastLoggerStatus = status;
                if (!errorEventSeen) workerBootPhase = status;
              }
              if (
                typeof message.progress === "number"
                && Number.isFinite(message.progress)
              ) {
                workerProgressListener?.(clamp(message.progress, 0, 1));
              }
            },
          }
        );
      }
    } catch (error: unknown) {
      lastFailure = error;
      attemptDiagnostic.outcome = "failed";
      attemptDiagnostic.finishedAtUtc = new Date().toISOString();
      attemptDiagnostic.durationMs = Math.max(0, performance.now() - attemptStartedMs);
      workerLastError = error instanceof Error ? error.message : String(error);
      workerBootPhase = "worker_create_failed";
      break;
    }

    try {
      const worker = await created;
      attemptDiagnostic.finishedAtUtc = new Date().toISOString();
      attemptDiagnostic.durationMs = Math.max(0, performance.now() - attemptStartedMs);

      // Ein Worker, dessen Boot bereits ein echtes ErrorEvent gemeldet hat, wird
      // niemals als gesund weiterverwendet. Erst vollstaendig terminieren, dann
      // (nur auf iOS und nur wenn vorhanden) den naechsten Core seriell starten.
      if (errorEventSeen) {
        try {
          await worker.terminate();
        } catch {
          // Der fehlgeschlagene Worker darf den seriellen Cleanup nicht blockieren.
        }
        lastFailure = new Error(workerLastError ?? "Tesseract worker error event");
        if (index + 1 < plan.length) continue;
        workerFallbackFailed = attempt > 1;
        break;
      }

      attemptDiagnostic.outcome = "success";
      workerReady = true;
      workerBootPhase = "initialized";
      workerBootFinishedAtUtc = new Date().toISOString();
      workerBootDurationMs = workerBootStartedAtMs === null
        ? null
        : Math.max(0, performance.now() - workerBootStartedAtMs);
      workerFallbackSucceeded = attempt > 1;
      if (attempt === 1) workerLastError = null;
      return worker;
    } catch (error: unknown) {
      lastFailure = error;
      attemptDiagnostic.finishedAtUtc = new Date().toISOString();
      attemptDiagnostic.durationMs = Math.max(0, performance.now() - attemptStartedMs);
      if (attemptDiagnostic.outcome !== "worker_error_event") attemptDiagnostic.outcome = "failed";
      workerLastError = error instanceof Error ? error.message : String(error);

      // Kein pauschaler Retry: Nur der dokumentierte fruehe Worker-ErrorEvent darf
      // auf iOS die konservativere Core-Variante aktivieren.
      if ((errorEventSeen || attemptDiagnostic.outcome === "worker_error_event") && index + 1 < plan.length) continue;
      if (attempt > 1) workerFallbackFailed = true;
      break;
    }
  }

  workerReady = false;
  workerBootPhase = workerLastErrorEvent ? "worker_error_event" : "failed";
  workerBootFinishedAtUtc = new Date().toISOString();
  workerBootDurationMs = workerBootStartedAtMs === null
    ? null
    : Math.max(0, performance.now() - workerBootStartedAtMs);
  throw formatOcrStartupError(lastFailure);
}

async function getWorker(): Promise<TesseractWorker> {
  if (workerTerminationPromise) {
    await workerTerminationPromise;
  }

  if (!workerPromise) {
    const generation = ++workerGeneration;
    workerReady = false;
    workerBootPhase = "worker_create_requested";
    workerBootStartedAtUtc = new Date().toISOString();
    workerBootStartedAtMs = performance.now();
    workerBootFinishedAtUtc = null;
    workerBootDurationMs = null;
    workerLastLoggerStatus = null;
    workerLastError = null;
    workerSelectedCoreMode = "automatic";
    workerSelectedCorePath = TESSERACT_CORE_PATH;
    workerAttemptNumber = 0;
    workerAttemptHistory = [];
    workerLastErrorEvent = null;
    workerFallbackStarted = false;
    workerFallbackSucceeded = false;
    workerFallbackFailed = false;
    workerTraineddataTransport = isIosWebKitOcrRuntime() ? "main-thread-preloaded" : "worker-fetch";
    workerLanguageAssetDiagnostics = [];
    workerIosLanguageAssets = null;

    const tracked = bootWorkerSequence().catch((error: unknown) => {
      if (workerPromise === tracked && generation === workerGeneration) {
        workerPromise = null;
        workerReady = false;
        workerProgressListener = null;
      }
      if (error instanceof Error && error.name === "OcrRuntimeInitializationError") throw error;
      throw formatOcrStartupError(error);
    });
    workerPromise = tracked;
  }

  const activeWorkerPromise = workerPromise;
  if (!activeWorkerPromise) {
    throw new Error("OCR-Worker konnte nicht initialisiert werden.");
  }
  return activeWorkerPromise;
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);

  try {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function prepareCanvas(image: HTMLImageElement, contrastFactor = 1.18): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, image.naturalWidth);
  canvas.height = Math.max(1, image.naturalHeight);

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("OCR-Arbeitsbild konnte nicht vorbereitet werden.");
  }

  context.drawImage(image, 0, 0);

  // Leichte Graustufen-/Kontrastnormalisierung. Die eigentliche Perspektiv-
  // korrektur kommt bereits aus OpenCV. CLAHE kann später noch als eigener
  // OpenCV-OCR-Preprocessing-Pass ergänzt werden.
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const gray = Math.round(
      pixels[index] * 0.299
      + pixels[index + 1] * 0.587
      + pixels[index + 2] * 0.114
    );
    const normalized = clamp(Math.round((gray - 128) * contrastFactor + 128), 0, 255);
    pixels[index] = normalized;
    pixels[index + 1] = normalized;
    pixels[index + 2] = normalized;
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
}


function assertOcrImagePlausible(
  canvas: HTMLCanvasElement,
  minimumMean = 18,
  maximumMean = 245,
  minimumStandardDeviation = 10
): void {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("OCR-Arbeitsbild konnte nicht geprüft werden.");
  }

  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  let sum = 0;
  let sumSquares = 0;
  let count = 0;

  for (let index = 0; index < data.length; index += 16) {
    const value = data[index];
    sum += value;
    sumSquares += value * value;
    count += 1;
  }

  const mean = count > 0 ? sum / count : 0;
  const variance = count > 0 ? Math.max(0, sumSquares / count - mean * mean) : 0;
  const standardDeviation = Math.sqrt(variance);

  if (
    mean < minimumMean
    || mean > maximumMean
    || standardDeviation < minimumStandardDeviation
  ) {
    throw new Error(
      `OCR übersprungen: Analysebild unplausibel (Helligkeit ${mean.toFixed(1)}, Streuung ${standardDeviation.toFixed(1)}).`
    );
  }
}

const OCR_REFERENCE_VERTICAL_LINES = VECTOR_TEMPLATE_VERTICAL_LINES;
const OCR_REFERENCE_HORIZONTAL_LINES = VECTOR_TEMPLATE_HORIZONTAL_LINES;

type GridAxisFit = {
  scale: number;
  offset: number;
  residual: number;
  matches: number;
};

function darkProjection(canvas: HTMLCanvasElement, axis: "x" | "y"): Float32Array {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return new Float32Array(0);
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  const length = axis === "x" ? canvas.width : canvas.height;
  const orthogonal = axis === "x" ? canvas.height : canvas.width;
  const projection = new Float32Array(length);
  const step = Math.max(1, Math.floor(orthogonal / 420));
  const threshold = 105;

  for (let primary = 0; primary < length; primary += 1) {
    let dark = 0;
    let samples = 0;
    for (let secondary = 0; secondary < orthogonal; secondary += step) {
      const x = axis === "x" ? primary : secondary;
      const y = axis === "x" ? secondary : primary;
      const value = data[(y * canvas.width + x) * 4];
      if (value <= threshold) dark += 1;
      samples += 1;
    }
    projection[primary] = samples > 0 ? dark / samples : 0;
  }
  return projection;
}

function locateGridAxisMatches(
  projection: Float32Array,
  expectedLines: readonly number[],
  minimumScore: number
): Array<{ expected: number; observed: number; score: number }> {
  if (projection.length < 4) return [];
  const last = projection.length - 1;
  const radius = Math.max(3, Math.round(last * 0.040));
  const matches: Array<{ expected: number; observed: number; score: number }> = [];

  for (const expected of expectedLines) {
    const center = Math.round(expected * last);
    const start = Math.max(0, center - radius);
    const end = Math.min(last, center + radius);
    let bestIndex = center;
    let bestScore = -1;
    for (let index = start; index <= end; index += 1) {
      const score = projection[index];
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    }
    if (bestScore >= minimumScore) {
      matches.push({ expected, observed: bestIndex / last, score: bestScore });
    }
  }
  return matches;
}

function fitGridAxis(matches: Array<{ expected: number; observed: number; score: number }>): GridAxisFit | null {
  if (matches.length < 2) return null;
  const weightSum = matches.reduce((sum, item) => sum + Math.max(0.05, item.score), 0);
  const meanExpected = matches.reduce((sum, item) => sum + item.expected * Math.max(0.05, item.score), 0) / weightSum;
  const meanObserved = matches.reduce((sum, item) => sum + item.observed * Math.max(0.05, item.score), 0) / weightSum;
  let numerator = 0;
  let denominator = 0;
  for (const item of matches) {
    const weight = Math.max(0.05, item.score);
    numerator += weight * (item.expected - meanExpected) * (item.observed - meanObserved);
    denominator += weight * (item.expected - meanExpected) ** 2;
  }
  if (denominator <= 1e-8) return null;
  const scale = numerator / denominator;
  const offset = meanObserved - scale * meanExpected;
  const residual = matches.reduce(
    (sum, item) => sum + Math.abs(item.observed - (scale * item.expected + offset)),
    0
  ) / matches.length;

  if (!Number.isFinite(scale) || !Number.isFinite(offset) || !Number.isFinite(residual)) return null;
  if (scale < 0.92 || scale > 1.08 || Math.abs(offset) > 0.055 || residual > 0.024) return null;
  return { scale, offset, residual, matches: matches.length };
}

function transformOcrRegion(
  region: NormalizedOcrRegion,
  horizontalFit: GridAxisFit,
  verticalFit: GridAxisFit
): NormalizedOcrRegion {
  const left = clamp(horizontalFit.scale * region.left + horizontalFit.offset, 0, 0.995);
  const right = clamp(horizontalFit.scale * (region.left + region.width) + horizontalFit.offset, left + 0.005, 1);
  const top = clamp(verticalFit.scale * region.top + verticalFit.offset, 0, 0.995);
  const bottom = clamp(verticalFit.scale * (region.top + region.height) + verticalFit.offset, top + 0.005, 1);
  return {
    ...region,
    left,
    top,
    width: right - left,
    height: bottom - top,
  };
}

function gridAnchorOcrRegions(
  canvas: HTMLCanvasElement,
  regions: readonly NormalizedOcrRegion[]
): { regions: NormalizedOcrRegion[]; details: ScanOcrGridAnchoringDetails } {
  const verticalMatches = locateGridAxisMatches(darkProjection(canvas, "x"), OCR_REFERENCE_VERTICAL_LINES, 0.16);
  const horizontalMatches = locateGridAxisMatches(darkProjection(canvas, "y"), OCR_REFERENCE_HORIZONTAL_LINES, 0.16);
  const horizontalFit = fitGridAxis(verticalMatches);
  const verticalFit = fitGridAxis(horizontalMatches);

  if (!horizontalFit || !verticalFit) {
    return {
      regions: regions.map((region) => ({ ...region })),
      details: {
        applied: false,
        horizontalMatches: verticalMatches.length,
        verticalMatches: horizontalMatches.length,
        horizontalScale: horizontalFit?.scale ?? null,
        horizontalOffset: horizontalFit?.offset ?? null,
        verticalScale: verticalFit?.scale ?? null,
        verticalOffset: verticalFit?.offset ?? null,
        meanResidual: horizontalFit && verticalFit ? (horizontalFit.residual + verticalFit.residual) / 2 : null,
        reason: "Rasterlinien nicht stabil genug für eine ROI-Korrektur; konfigurierte ROIs bleiben aktiv.",
      },
    };
  }

  const meanResidual = (horizontalFit.residual + verticalFit.residual) / 2;
  return {
    regions: regions.map((region) => transformOcrRegion(region, horizontalFit, verticalFit)),
    details: {
      applied: true,
      horizontalMatches: verticalMatches.length,
      verticalMatches: horizontalMatches.length,
      horizontalScale: horizontalFit.scale,
      horizontalOffset: horizontalFit.offset,
      verticalScale: verticalFit.scale,
      verticalOffset: verticalFit.offset,
      meanResidual,
      reason: `Raster-Affine aktiv · X ${verticalMatches.length}/${OCR_REFERENCE_VERTICAL_LINES.length} · Y ${horizontalMatches.length}/${OCR_REFERENCE_HORIZONTAL_LINES.length} · Residual ${(meanResidual * 100).toFixed(2)} %.`,
    },
  };
}

function storageSubcellOcrRegions(
  gridDetails: ScanOcrGridAnchoringDetails
): Array<{ name: ScanStorageAddressSubcellName; region: NormalizedOcrRegion }> {
  return STORAGE_SUBCELL_ORDER.map((name) => {
    const source = VECTOR_TEMPLATE_STORAGE_SUBCELL_ROIS[name];
    // The generated SR26 vector contract is emitted with `as const`, so its
    // ROI coordinates are literal types. SR27 remaps those values through the
    // Smart-Grid affine at runtime; widen the local copies deliberately.
    let left: number = source.left;
    let top: number = source.top;
    let width: number = source.width;
    let height: number = source.height;

    if (
      gridDetails.applied
      && gridDetails.horizontalScale !== null
      && gridDetails.horizontalOffset !== null
      && gridDetails.verticalScale !== null
      && gridDetails.verticalOffset !== null
    ) {
      const right = clamp(
        gridDetails.horizontalScale * (source.left + source.width) + gridDetails.horizontalOffset,
        0.005,
        1
      );
      const bottom = clamp(
        gridDetails.verticalScale * (source.top + source.height) + gridDetails.verticalOffset,
        0.005,
        1
      );
      left = clamp(gridDetails.horizontalScale * source.left + gridDetails.horizontalOffset, 0, 0.995);
      top = clamp(gridDetails.verticalScale * source.top + gridDetails.verticalOffset, 0, 0.995);
      width = Math.max(0.005, right - left);
      height = Math.max(0.005, bottom - top);
    }

    return {
      name,
      region: {
        field: "storageAddress",
        label: STORAGE_SUBCELL_LABELS[name],
        left,
        top,
        width,
        height,
        mode: "line",
        whitelist: STORAGE_SUBCELL_WHITELISTS[name],
      },
    };
  });
}

function toRectangle(
  region: NormalizedOcrRegion,
  width: number,
  height: number
): ScanOcrFieldResult["rectangle"] {
  const left = Math.round(region.left * width);
  const top = Math.round(region.top * height);
  const rectangleWidth = Math.max(1, Math.round(region.width * width));
  const rectangleHeight = Math.max(1, Math.round(region.height * height));

  return {
    left: clamp(left, 0, width - 1),
    top: clamp(top, 0, height - 1),
    width: Math.min(rectangleWidth, width - left),
    height: Math.min(rectangleHeight, height - top),
  };
}

function cleanWhitespace(value: string): string {
  return value
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeMaterialNumber(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, "");
  const exact = digits.match(/9830\d{6}/);
  if (exact) {
    return exact[0];
  }

  const anyTenDigits = digits.match(/\d{10}/);
  return anyTenDigits?.[0] ?? digits.slice(0, 10);
}

function normalizeField(field: ScanOcrFieldResult["field"], raw: string): string {
  switch (field) {
    case "materialNumber":
      return normalizeMaterialNumber(raw);
    case "storageAddress":
      return normalizeStorageAddress(raw);
    case "materialType":
      return cleanWhitespace(raw).replace(/\s*-\s*/g, "-");
    case "materialText":
      return cleanWhitespace(raw);
  }
}

function cropRegionCanvasLegacy(canvas: HTMLCanvasElement, rectangle: ScanOcrFieldResult["rectangle"]): HTMLCanvasElement {
  const cropped = document.createElement("canvas");
  cropped.width = Math.max(1, rectangle.width);
  cropped.height = Math.max(1, rectangle.height);
  const context = cropped.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("OCR-Bereich konnte nicht vorbereitet werden.");
  context.drawImage(canvas, rectangle.left, rectangle.top, rectangle.width, rectangle.height, 0, 0, rectangle.width, rectangle.height);
  return cropped;
}

function cropRegionCanvasCanonical(canvas: HTMLCanvasElement, rectangle: ScanOcrFieldResult["rectangle"]): HTMLCanvasElement {
  const cropped = document.createElement("canvas");
  cropped.width = Math.max(1, rectangle.width);
  cropped.height = Math.max(1, rectangle.height);
  const sourceContext = canvas.getContext("2d", { willReadFrequently: true });
  const context = cropped.getContext("2d", { willReadFrequently: true });
  if (!sourceContext || !context) throw new Error("OCR-Bereich konnte nicht vorbereitet werden.");
  // v3-Runtime: exakte 1:1-Pixelkopie. Alte v1/v2-Bundles behalten bewusst
  // den historischen Canvas-drawImage-Pfad, damit ein Frontend-Update Production nicht still verändert.
  context.putImageData(sourceContext.getImageData(rectangle.left, rectangle.top, rectangle.width, rectangle.height), 0, 0);
  return cropped;
}

function preprocessOcrRegionLegacy(source: HTMLCanvasElement, parameters: Record<string, number>): HTMLCanvasElement {
  const scale = Math.max(1, Math.min(4, parameters.OCR_UPSCALE ?? 1));
  const target = document.createElement("canvas");
  target.width = Math.max(1, Math.round(source.width * scale));
  target.height = Math.max(1, Math.round(source.height * scale));
  const ctx = target.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("OCR-Vorverarbeitung konnte nicht vorbereitet werden.");
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(source, 0, 0, target.width, target.height);
  const threshold = Math.round(parameters.OCR_THRESHOLD ?? 0);
  const sharpen = Math.max(0, Math.min(2, (parameters.OCR_SHARPEN ?? 1) - 1));
  if (threshold > 0 || sharpen > 0.01) {
    const image = ctx.getImageData(0, 0, target.width, target.height);
    const data = image.data;
    if (threshold > 0) {
      for (let i = 0; i < data.length; i += 4) { const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]; const value = gray >= threshold ? 255 : 0; data[i] = value; data[i + 1] = value; data[i + 2] = value; }
    } else if (sharpen > 0.01 && target.width > 2 && target.height > 2) {
      const copy = new Uint8ClampedArray(data);
      const w = target.width;
      for (let y = 1; y < target.height - 1; y++) for (let x = 1; x < w - 1; x++) {
        const at = (y * w + x) * 4;
        for (let c = 0; c < 3; c++) { const center = copy[at + c]; const neighbors = copy[at - 4 + c] + copy[at + 4 + c] + copy[at - w * 4 + c] + copy[at + w * 4 + c]; data[at + c] = Math.max(0, Math.min(255, center * (1 + 4 * sharpen) - neighbors * sharpen)); }
      }
    }
    ctx.putImageData(image, 0, 0);
  }
  return target;
}

function preprocessOcrRegionCanonical(source: HTMLCanvasElement, parameters: Record<string, number>): HTMLCanvasElement {
  const scale = Math.max(1, Math.min(4, parameters.OCR_UPSCALE ?? 1));
  const target = document.createElement("canvas");
  target.width = Math.max(1, Math.round(source.width * scale));
  target.height = Math.max(1, Math.round(source.height * scale));
  const sourceContext = source.getContext("2d", { willReadFrequently: true });
  const ctx = target.getContext("2d", { willReadFrequently: true });
  if (!sourceContext || !ctx) throw new Error("OCR-Vorverarbeitung konnte nicht vorbereitet werden.");
  const sourcePixels = sourceContext.getImageData(0, 0, source.width, source.height).data;
  const image = ctx.createImageData(target.width, target.height);
  const data = image.data;
  const scaleX = source.width / target.width, scaleY = source.height / target.height;
  for (let y = 0; y < target.height; y++) {
    const sy = Math.max(0, Math.min(source.height - 1, (y + .5) * scaleY - .5));
    const y0 = Math.floor(sy), y1 = Math.min(source.height - 1, y0 + 1), wy = sy - y0;
    for (let x = 0; x < target.width; x++) {
      const sx = Math.max(0, Math.min(source.width - 1, (x + .5) * scaleX - .5));
      const x0 = Math.floor(sx), x1 = Math.min(source.width - 1, x0 + 1), wx = sx - x0;
      const top = sourcePixels[(y0 * source.width + x0) * 4] * (1 - wx) + sourcePixels[(y0 * source.width + x1) * 4] * wx;
      const bottom = sourcePixels[(y1 * source.width + x0) * 4] * (1 - wx) + sourcePixels[(y1 * source.width + x1) * 4] * wx;
      const value = clamp(Math.round(top * (1 - wy) + bottom * wy), 0, 255);
      const at = (y * target.width + x) * 4; data[at] = value; data[at + 1] = value; data[at + 2] = value; data[at + 3] = 255;
    }
  }
  const threshold = Math.round(parameters.OCR_THRESHOLD ?? 0);
  const sharpen = Math.max(0, Math.min(2, (parameters.OCR_SHARPEN ?? 1) - 1));
  if (threshold > 0) {
    for (let i = 0; i < data.length; i += 4) { const value = data[i] >= threshold ? 255 : 0; data[i] = value; data[i + 1] = value; data[i + 2] = value; }
  } else if (sharpen > 0.01 && target.width > 2 && target.height > 2) {
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < target.height - 1; y++) for (let x = 1; x < target.width - 1; x++) {
      const at = (y * target.width + x) * 4;
      const center = copy[at]; const neighbors = copy[at - 4] + copy[at + 4] + copy[at - target.width * 4] + copy[at + target.width * 4];
      const value = clamp(Math.round(center * (1 + 4 * sharpen) - neighbors * sharpen), 0, 255);
      data[at] = value; data[at + 1] = value; data[at + 2] = value;
    }
  }
  ctx.putImageData(image, 0, 0);
  return target;
}

function psmForRegion(region: NormalizedOcrRegion, parameters: Record<string, number>) {
  const configured = Math.round(region.mode === "line" ? (parameters.OCR_PSM_LINE ?? 7) : (parameters.OCR_PSM_BLOCK ?? 6));
  const psm = { 6: PSM.SINGLE_BLOCK, 7: PSM.SINGLE_LINE, 8: PSM.SINGLE_WORD, 11: PSM.SPARSE_TEXT, 13: PSM.RAW_LINE } as Record<number, string>;
  return psm[configured] ?? (region.mode === "line" ? PSM.SINGLE_LINE : PSM.SINGLE_BLOCK);
}

function expandRectangle(
  rectangle: ScanOcrFieldResult["rectangle"],
  canvas: HTMLCanvasElement,
  xRatio: number,
  yRatio: number
): ScanOcrFieldResult["rectangle"] {
  const dx = Math.max(1, Math.round(rectangle.width * xRatio));
  const dy = Math.max(1, Math.round(rectangle.height * yRatio));
  const left = Math.max(0, rectangle.left - dx);
  const top = Math.max(0, rectangle.top - dy);
  const right = Math.min(canvas.width, rectangle.left + rectangle.width + dx);
  const bottom = Math.min(canvas.height, rectangle.top + rectangle.height + dy);
  return { left, top, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
}


async function sha256CanvasGray(canvas: HTMLCanvasElement): Promise<string> {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("OCR-Evidence konnte nicht gelesen werden.");
  const rgba = context.getImageData(0, 0, canvas.width, canvas.height).data;
  const gray = new Uint8Array(canvas.width * canvas.height);
  for (let index = 0, pixel = 0; index < rgba.length; index += 4, pixel += 1) gray[pixel] = rgba[index];
  const digest = await crypto.subtle.digest("SHA-256", gray);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

async function recognizeRegionOnce(
  worker: TesseractWorker,
  canvas: HTMLCanvasElement,
  region: NormalizedOcrRegion,
  parameters: Record<string, number>,
  canonicalRuntime = false,
  rectangleOverride?: ScanOcrFieldResult["rectangle"]
): Promise<ScanOcrFieldResult> {
  const startedMs = performance.now();
  const psm = psmForRegion(region, parameters);
  await worker.setParameters({
    tessedit_pageseg_mode: psm as typeof PSM.SINGLE_BLOCK,
    preserve_interword_spaces: "1",
    ...(region.whitelist
      ? { tessedit_char_whitelist: region.whitelist }
      : { tessedit_char_whitelist: "" }),
    ...TESSERACT_DETERMINISTIC_PARAMETERS,
  } as Parameters<TesseractWorker["setParameters"]>[0]);

  const rectangle = rectangleOverride ?? toRectangle(region, canvas.width, canvas.height);
  const source = canonicalRuntime
    ? cropRegionCanvasCanonical(canvas, rectangle)
    : cropRegionCanvasLegacy(canvas, rectangle);
  const cropped = canonicalRuntime
    ? preprocessOcrRegionCanonical(source, parameters)
    : preprocessOcrRegionLegacy(source, parameters);
  const inputEvidence = {
    width: cropped.width,
    height: cropped.height,
    sha256: await sha256CanvasGray(cropped),
    psm: Number(psm),
    whitelist: region.whitelist ?? "",
  };
  const result = await worker.recognize(cropped);
  const rawText = result.data.text ?? "";

  return {
    field: region.field,
    label: region.label,
    text: normalizeField(region.field, rawText),
    rawText: cleanWhitespace(rawText),
    confidence:
      typeof result.data.confidence === "number"
        ? result.data.confidence
        : null,
    durationMs: Math.round(performance.now() - startedMs),
    rectangle,
    inputEvidence,
  };
}

const STORAGE_DIGIT_CONFUSIONS: Record<string, string> = {
  O: "0", Q: "0", D: "0",
  I: "1", L: "1",
  Z: "2", S: "5", G: "6", B: "8",
};
const STORAGE_LETTER_CONFUSIONS: Record<string, string> = {
  "0": "O", "1": "I", "2": "Z", "5": "S", "6": "G", "8": "B",
};

function normalizeStorageCellRaw(raw: string): string {
  return raw.normalize("NFKC").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function decodeStorageDigits(raw: string, expectedLength: number): { value: string; corrections: string[] } {
  const compact = normalizeStorageCellRaw(raw);
  if (compact.length !== expectedLength) return { value: "", corrections: [] };
  const corrections: string[] = [];
  let value = "";
  for (let index = 0; index < compact.length; index += 1) {
    const char = compact[index];
    if (/\d/.test(char)) {
      value += char;
      continue;
    }
    const mapped = STORAGE_DIGIT_CONFUSIONS[char];
    if (!mapped) return { value: "", corrections: [] };
    corrections.push(`${char}→${mapped}@${index + 1}`);
    value += mapped;
  }
  return { value, corrections };
}

function decodeStorageBuilding(raw: string): { value: string; corrections: string[] } {
  const compact = normalizeStorageCellRaw(raw);
  if (compact.length !== 3) return { value: "", corrections: [] };
  const corrections: string[] = [];
  let prefix = "";
  for (let index = 0; index < 2; index += 1) {
    const char = compact[index];
    if (/\d/.test(char)) {
      prefix += char;
      continue;
    }
    const mapped = STORAGE_DIGIT_CONFUSIONS[char];
    if (!mapped) return { value: "", corrections: [] };
    corrections.push(`${char}→${mapped}@${index + 1}`);
    prefix += mapped;
  }
  const floorRaw = compact[2];
  let floor = floorRaw;
  if (!/[A-Z]/.test(floorRaw)) {
    floor = STORAGE_LETTER_CONFUSIONS[floorRaw] ?? "";
    if (!floor) return { value: "", corrections: [] };
    corrections.push(`${floorRaw}→${floor}@3`);
  }
  return { value: `${prefix}${floor}`, corrections };
}

function skippedStorageDecoder(reason: string): ScanStorageAddressDecoderDetails {
  return {
    contractVersion: STORAGE_ADDRESS_DECODER_CONTRACT,
    source: "SR26_VECTOR_TEMPLATE_SUBCELLS",
    attempted: false,
    selected: false,
    valid: false,
    candidate: "",
    meanConfidence: null,
    durationMs: 0,
    reason,
    cells: [],
  };
}

async function recognizeStorageAddressSubcells(
  worker: TesseractWorker,
  canvas: HTMLCanvasElement,
  subcells: Array<{ name: ScanStorageAddressSubcellName; region: NormalizedOcrRegion }>,
  parameters: Record<string, number>,
  canonicalRuntime: boolean
): Promise<ScanStorageAddressDecoderDetails> {
  const startedMs = performance.now();
  const cells: ScanStorageAddressSubcellResult[] = [];

  for (const { name, region } of subcells) {
    const result = await recognizeRegionOnce(worker, canvas, region, parameters, canonicalRuntime);
    const decoded = name === "building"
      ? decodeStorageBuilding(result.rawText)
      : decodeStorageDigits(result.rawText, name === "cabinet" ? 3 : 2);
    cells.push({
      name,
      label: STORAGE_SUBCELL_LABELS[name],
      rawText: result.rawText,
      decodedText: decoded.value,
      confidence: result.confidence,
      durationMs: result.durationMs ?? 0,
      valid: decoded.value.length > 0,
      correctionsApplied: decoded.corrections,
      rectangle: result.rectangle,
      inputEvidence: result.inputEvidence,
    });
  }

  const byName = new Map(cells.map((cell) => [cell.name, cell]));
  const compact = [
    byName.get("building")?.decodedText ?? "",
    byName.get("cabinet")?.decodedText ?? "",
    byName.get("compartment")?.decodedText ?? "",
    byName.get("subCompartment")?.decodedText ?? "",
  ].join("");
  const candidate = normalizeStorageAddress(compact);
  const confidences = cells
    .map((cell) => cell.confidence)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const meanConfidence = confidences.length > 0
    ? confidences.reduce((sum, value) => sum + value, 0) / confidences.length
    : null;

  return {
    contractVersion: STORAGE_ADDRESS_DECODER_CONTRACT,
    source: "SR26_VECTOR_TEMPLATE_SUBCELLS",
    attempted: true,
    selected: false,
    valid: candidate.length > 0,
    candidate,
    meanConfidence,
    durationMs: Math.round(performance.now() - startedMs),
    reason: candidate
      ? "Vier SR26-Vektorzellen ergeben eine vollständige materialstammunabhängige Lagerort-Grammatik."
      : "Mindestens eine SR26-Vektorzelle konnte nicht eindeutig in die Lagerort-Grammatik dekodiert werden.",
    cells,
  };
}

function storageDecoderPass(details: ScanStorageAddressDecoderDetails): ScanOcrPassResult {
  return {
    pass: "subcells",
    text: details.candidate,
    rawText: details.cells.map((cell) => cell.rawText).join(" | "),
    confidence: details.meanConfidence,
    durationMs: details.durationMs,
    score: details.valid ? 1800 + (details.meanConfidence ?? 0) : 0,
  };
}

function criticalFieldScore(field: ScanOcrFieldResult): number {
  const confidence = field.confidence ?? 0;
  if (field.field === "materialNumber") {
    const compact = field.text.replace(/\s+/g, "");
    if (/^983\d{7}$/.test(compact)) return 2000 + confidence;
    if (/^\d{10}$/.test(compact)) return 1000 + confidence;
    if (compact.startsWith("983")) return 500 + confidence;
    return confidence - Math.abs(10 - compact.length) * 8;
  }
  if (field.field === "storageAddress") {
    const normalized = normalizeStorageAddress(field.text);
    if (isValidStorageAddress(normalized)) return 1800 + confidence;
    const tokenCount = normalized.split(/\s+/).filter(Boolean).length;
    return confidence + tokenCount * 45;
  }
  return confidence;
}

function passResult(pass: string, field: ScanOcrFieldResult): ScanOcrPassResult {
  return {
    pass,
    text: field.text,
    rawText: field.rawText,
    confidence: field.confidence,
    durationMs: field.durationMs ?? 0,
    score: criticalFieldScore(field),
    inputEvidence: field.inputEvidence,
  };
}

function criticalFieldIsStrong(field: ScanOcrFieldResult, parameters: Record<string, number>): boolean {
  if (field.field === "materialNumber") {
    return /^983\d{7}$/.test(field.text) && (field.confidence ?? 0) >= (parameters.OCR_STRONG_CONFIDENCE_MATERIAL_NUMBER ?? 88);
  }
  if (field.field === "storageAddress") {
    return isValidStorageAddress(field.text) && (field.confidence ?? 0) >= (parameters.OCR_STRONG_CONFIDENCE_STORAGE_ADDRESS ?? 84);
  }
  return true;
}

function thresholdVariant(parameters: Record<string, number>): Record<string, number> {
  const current = Math.round(parameters.OCR_THRESHOLD ?? 0);
  const fallbackBase = Math.round(parameters.OCR_THRESHOLD_FALLBACK_BASE ?? 165);
  const fallbackStep = Math.max(1, Math.round(parameters.OCR_THRESHOLD_FALLBACK_STEP ?? 25));
  const fallbackPivot = Math.round(parameters.OCR_THRESHOLD_FALLBACK_PIVOT ?? 150);
  const minUpscale = Math.max(1, parameters.OCR_MULTIPASS_MIN_UPSCALE ?? 2);
  const next = current <= 0
    ? fallbackBase
    : current < fallbackPivot
      ? current + fallbackStep
      : Math.max(0, current - fallbackStep);
  return {
    ...parameters,
    OCR_THRESHOLD: next,
    OCR_UPSCALE: Math.max(minUpscale, parameters.OCR_UPSCALE ?? 1),
  };
}

function segmentationVariant(
  parameters: Record<string, number>,
  field: ScanOcrFieldResult["field"]
): Record<string, number> {
  const minUpscale = Math.max(1, parameters.OCR_MULTIPASS_MIN_UPSCALE ?? 2);
  return {
    ...parameters,
    OCR_UPSCALE: Math.max(minUpscale, parameters.OCR_UPSCALE ?? 1),
    ...(field === "materialNumber"
      ? { OCR_PSM_LINE: Math.round(parameters.OCR_SEGMENTATION_PSM_LINE ?? 13) }
      : { OCR_PSM_BLOCK: Math.round(parameters.OCR_SEGMENTATION_PSM_BLOCK ?? 11) }),
  };
}

async function recognizeRegionSmart(
  worker: TesseractWorker,
  canvas: HTMLCanvasElement,
  region: NormalizedOcrRegion,
  parameters: Record<string, number>,
  canonicalRuntime: boolean,
  roiSource: "configured" | "grid-anchored",
  storageSubcells: Array<{ name: ScanStorageAddressSubcellName; region: NormalizedOcrRegion }> = []
): Promise<ScanOcrFieldResult> {
  const base = await recognizeRegionOnce(worker, canvas, region, parameters, canonicalRuntime);
  const critical = region.field === "materialNumber" || region.field === "storageAddress";
  if (!critical) {
    return {
      ...base,
      roiSource,
      selectedPass: "base",
      passCount: 1,
      passes: [passResult("base", base)],
    };
  }

  if (criticalFieldIsStrong(base, parameters)) {
    return {
      ...base,
      roiSource,
      selectedPass: "base",
      passCount: 1,
      passes: [passResult("base", base)],
      ...(region.field === "storageAddress"
        ? { storageAddressDecoder: skippedStorageDecoder("BLOCK_STRONG_SUBCELL_FALLBACK_NOT_REQUIRED") }
        : {}),
    };
  }

  let storageDecoder: ScanStorageAddressDecoderDetails | null = null;
  if (region.field === "storageAddress" && storageSubcells.length === STORAGE_SUBCELL_ORDER.length) {
    storageDecoder = await recognizeStorageAddressSubcells(
      worker,
      canvas,
      storageSubcells,
      parameters,
      canonicalRuntime
    );
    const blockNormalized = normalizeStorageAddress(base.text);
    if (!blockNormalized && storageDecoder.valid) {
      const selectedDecoder = {
        ...storageDecoder,
        selected: true,
        reason: "BLOCK_INVALID_SUBCELL_GRAMMAR_VALID",
      } satisfies ScanStorageAddressDecoderDetails;
      const decoderPass = storageDecoderPass(selectedDecoder);
      return {
        ...base,
        text: selectedDecoder.candidate,
        rawText: selectedDecoder.cells.map((cell) => cell.rawText).join(" | "),
        confidence: selectedDecoder.meanConfidence,
        durationMs: (base.durationMs ?? 0) + selectedDecoder.durationMs,
        roiSource,
        selectedPass: "subcells",
        passCount: 2,
        passes: [passResult("base", base), decoderPass],
        storageAddressDecoder: selectedDecoder,
      };
    }
    storageDecoder = {
      ...storageDecoder,
      reason: blockNormalized
        ? (storageDecoder.valid && storageDecoder.candidate !== blockNormalized
          ? "BLOCK_VALID_SUBCELL_CONFLICT_KEEP_BLOCK"
          : "BLOCK_VALID_SUBCELL_DIAGNOSTIC_ONLY")
        : "SUBCELL_GRAMMAR_INCOMPLETE_KEEP_BLOCK_MULTIPASS",
    };
  } else if (region.field === "storageAddress") {
    storageDecoder = skippedStorageDecoder("SUBCELL_CONTRACT_UNAVAILABLE_KEEP_BLOCK_MULTIPASS");
  }

  const attempts: Array<{ name: string; field: ScanOcrFieldResult }> = [{ name: "base", field: base }];
  const thresholdPass = await recognizeRegionOnce(
    worker,
    canvas,
    region,
    thresholdVariant(parameters),
    canonicalRuntime
  );
  attempts.push({ name: "threshold", field: thresholdPass });

  let best = attempts.reduce((winner, item) =>
    criticalFieldScore(item.field) > criticalFieldScore(winner.field) ? item : winner
  );

  if (!criticalFieldIsStrong(best.field, parameters)) {
    const baseRectangle = toRectangle(region, canvas.width, canvas.height);
    const expandedRectangle = expandRectangle(
      baseRectangle,
      canvas,
      parameters.OCR_EXPAND_X_RATIO ?? 0.018,
      parameters.OCR_EXPAND_Y_RATIO ?? 0.045
    );
    const segmentationPass = await recognizeRegionOnce(
      worker,
      canvas,
      region,
      segmentationVariant(parameters, region.field),
      canonicalRuntime,
      expandedRectangle
    );
    attempts.push({ name: "segmentation", field: segmentationPass });
    best = attempts.reduce((winner, item) =>
      criticalFieldScore(item.field) > criticalFieldScore(winner.field) ? item : winner
    );
  }

  const decoderDuration = storageDecoder?.attempted ? storageDecoder.durationMs : 0;
  return {
    ...best.field,
    durationMs: attempts.reduce((sum, item) => sum + (item.field.durationMs ?? 0), 0) + decoderDuration,
    roiSource,
    selectedPass: best.name,
    passCount: attempts.length + (storageDecoder?.attempted ? 1 : 0),
    passes: [
      ...attempts.map((item) => passResult(item.name, item.field)),
      ...(storageDecoder?.attempted ? [storageDecoderPass(storageDecoder)] : []),
    ],
    ...(region.field === "storageAddress" ? { storageAddressDecoder: storageDecoder } : {}),
  };
}

export interface LabelOcrResult {
  data: RecognizedLabelData;
  details: ScanOcrDetails;
}

export interface LabelOcrMaterialNumberProbe {
  text: string;
  rawText: string;
  confidence: number | null;
  durationMs: number;
}

export async function recognizeLabelMaterialNumber(
  file: File,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">
): Promise<LabelOcrMaterialNumberProbe> {
  const startedMs = performance.now();
  const worker = await getWorker();
  const effectiveRuntime = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
  const configuredRegions = effectiveRuntime.ocr.regions as readonly NormalizedOcrRegion[];
  const parameters = effectiveRuntime.ocr.params ?? {};
  const image = await loadImage(file);
  const canvas = prepareCanvas(image, parameters.OCR_CONTRAST_FACTOR ?? 1.18);
  assertOcrImagePlausible(
    canvas,
    parameters.OCR_MIN_MEAN ?? 18,
    parameters.OCR_MAX_MEAN ?? 245,
    parameters.OCR_MIN_STDDEV ?? 10
  );
  const smartRuntime = effectiveRuntime.ocr.smartGridMultiPass ?? false;
  const anchored = smartRuntime
    ? gridAnchorOcrRegions(canvas, configuredRegions)
    : { regions: configuredRegions.map((candidate) => ({ ...candidate })), details: null };
  const region = anchored.regions.find((candidate) => candidate.field === "materialNumber")
    ?? OCR_REGIONS.find((candidate) => candidate.field === "materialNumber");
  if (!region) throw new Error("Materialnummer-ROI ist nicht konfiguriert.");
  const field = smartRuntime
    ? await recognizeRegionSmart(
        worker,
        canvas,
        region,
        parameters,
        effectiveRuntime.ocr.canonicalPreprocess ?? false,
        anchored.details?.applied ? "grid-anchored" : "configured"
      )
    : await recognizeRegionOnce(
        worker,
        canvas,
        region,
        parameters,
        effectiveRuntime.ocr.canonicalPreprocess ?? false
      );
  return {
    text: field.text,
    rawText: field.rawText,
    confidence: field.confidence,
    durationMs: Math.round(performance.now() - startedMs),
  };
}

export async function recognizeLabelData(
  file: File,
  onProgress?: (progress: number) => void,
  regionOverrides?: VisualLabOcrRegion[],
  parameterOverrides?: Record<string, number>,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">
): Promise<LabelOcrResult> {
  const startedAt = new Date();
  const startedMs = performance.now();
  const worker = await getWorker();
  // Production-OCR verwendet denselben beim Capture eingefrorenen Runtime-Snapshot
  // wie Mapping und Detection. Ohne übergebenen Snapshot wird für eigenständige
  // Aufrufer einmalig ein lokaler Snapshot erzeugt. Visual-Lab-Overrides bleiben
  // davon bewusst unabhängig.
  const useRuntimeSnapshot = !regionOverrides && !parameterOverrides;
  const effectiveRuntime = useRuntimeSnapshot
    ? (runtimeSnapshot ?? await createVisionRuntimeSnapshot())
    : null;
  const canonicalOcrRuntime = effectiveRuntime?.ocr.canonicalPreprocess ?? false;
  const smartOcrRuntime = effectiveRuntime?.ocr.smartGridMultiPass ?? false;
  const configuredRegions =
    (regionOverrides ?? effectiveRuntime?.ocr.regions ?? OCR_REGIONS) as readonly NormalizedOcrRegion[];
  const priority: Record<ScanOcrFieldResult["field"], number> = { materialNumber: 0, storageAddress: 1, materialType: 2, materialText: 3 };
  const parameters = parameterOverrides ?? effectiveRuntime?.ocr.params ?? {};
  const image = await loadImage(file);
  const canvas = prepareCanvas(image, parameters.OCR_CONTRAST_FACTOR ?? 1.18);
  assertOcrImagePlausible(
    canvas,
    parameters.OCR_MIN_MEAN ?? 18,
    parameters.OCR_MAX_MEAN ?? 245,
    parameters.OCR_MIN_STDDEV ?? 10
  );
  const gridPlan = smartOcrRuntime
    ? gridAnchorOcrRegions(canvas, configuredRegions)
    : {
        regions: configuredRegions.map((region) => ({ ...region })),
        details: {
          applied: false,
          horizontalMatches: 0,
          verticalMatches: 0,
          horizontalScale: null,
          horizontalOffset: null,
          verticalScale: null,
          verticalOffset: null,
          meanResidual: null,
          reason: "Production-Runtime verwendet konfigurierte ROIs ohne Smart-Grid-Anker.",
        } satisfies ScanOcrGridAnchoringDetails,
      };
  const regions = [...gridPlan.regions].sort((a, b) => priority[a.field] - priority[b.field]);
  const storageSubcells = smartOcrRuntime ? storageSubcellOcrRegions(gridPlan.details) : [];
  const fields: ScanOcrFieldResult[] = [];

  workerProgressListener = (workerProgress) => {
    const completed = fields.length / regions.length;
    const currentShare = workerProgress / regions.length;
    onProgress?.(clamp(completed + currentShare, 0, 0.99));
  };

  try {
    for (const region of regions) {
      const field = smartOcrRuntime
        ? await recognizeRegionSmart(
            worker,
            canvas,
            region,
            parameters,
            canonicalOcrRuntime,
            gridPlan.details.applied ? "grid-anchored" : "configured",
            region.field === "storageAddress" ? storageSubcells : []
          )
        : {
            ...await recognizeRegionOnce(worker, canvas, region, parameters, canonicalOcrRuntime),
            roiSource: "configured" as const,
            selectedPass: "base",
            passCount: 1,
          };
      fields.push(field);
      onProgress?.(fields.length / regions.length);
    }
  } finally {
    workerProgressListener = null;
  }

  const byField = new Map(fields.map((field) => [field.field, field.text]));
  const finishedAt = new Date();

  return {
    data: {
      materialText: byField.get("materialText") ?? "",
      materialType: byField.get("materialType") ?? "",
      materialNumber: byField.get("materialNumber") ?? "",
      // Bestellinfo ist absichtlich ein reines manuelles Eingabefeld.
      orderInfo: "",
      storageAddress: byField.get("storageAddress") ?? "",
    },
    details: {
      engine: "tesseract.js",
      languages: OCR_LANGUAGES,
      status: "done",
      startedAt: startedAt.toISOString(),
      finishedAt: finishedAt.toISOString(),
      durationMs: Math.round(performance.now() - startedMs),
      progress: 1,
      fields,
      gridAnchoring: gridPlan.details,
      masterResolution: null,
      error: null,
    },
  };
}

export async function terminateLabelOcr(): Promise<void> {
  if (workerTerminationPromise) {
    await workerTerminationPromise;
    return;
  }

  const activeWorkerPromise = workerPromise;
  workerReady = false;
  workerProgressListener = null;
  if (!activeWorkerPromise) {
    workerBootPhase = "cold";
    return;
  }

  // v0.282.7: Ein noch startender Tesseract-Worker bleibt Eigentümer seines
  // createWorker()-Promises. Wir setzen workerPromise NICHT vorzeitig auf null;
  // dadurch kann parallel niemals ein zweiter produktiver WASM-Start entstehen.
  const termination: Promise<void> = (async () => {
    try {
      const worker = await activeWorkerPromise;
      await worker.terminate();
    } catch {
      // Ein fehlgeschlagener Start gilt als beendet. Entscheidend ist, dass kein
      // zweiter Worker neben einem noch laufenden createWorker() erzeugt wird.
    } finally {
      if (workerPromise === activeWorkerPromise) workerPromise = null;
      workerReady = false;
      workerProgressListener = null;
      workerBootPhase = "cold";
    }
  })();
  workerTerminationPromise = termination;
  try {
    await termination;
  } finally {
    if (workerTerminationPromise === termination) workerTerminationPromise = null;
  }
}

// PV-2 / v0.268.0 -----------------------------------------------------------------
// Additive Same-RAW OCR shadow candidate. This block is deliberately kept outside
// the serial Production Recognition functions above. The Production path still
// calls recognizeLabelData() exactly once with its single warm worker. PV-2 is
// invoked only after that serial result is final and can neither replace nor
// mutate business Recognition output.
const PV2_SHADOW_BEGIN = "PV2_SAME_RAW_PARALLEL_SHADOW_BEGIN" as const;
void PV2_SHADOW_BEGIN;

const PV2_SHADOW_POOL_IDLE_MS = 120000;
let pv2ShadowWorkerPoolPromise: Promise<TesseractWorker[]> | null = null;
let pv2ShadowWorkerPoolReady = false;
let pv2ShadowWorkerPoolUseCount = 0;
let pv2ShadowWorkerPoolIdleTimer: number | null = null;

export function isLabelOcrParallelShadowRuntimeWarm(): boolean {
  return pv2ShadowWorkerPoolReady;
}

type ParallelShadowCandidateOptions = {
  recognitionRunId?: string | null;
};

function roundShadowMetric(value: number | null, digits = 1): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

async function sha256BlobForShadow(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

function confidenceEqual(left: number | null | undefined, right: number | null | undefined): boolean {
  if (left == null || right == null) return left == null && right == null;
  return Math.abs(left - right) <= 1e-6;
}

function stableShadowPassSignature(field: ScanOcrFieldResult): string {
  return JSON.stringify((field.passes ?? []).map((pass) => ({
    pass: pass.pass,
    text: pass.text,
    rawText: pass.rawText,
    confidence: pass.confidence,
    score: pass.score,
    inputSha256: pass.inputEvidence?.sha256 ?? null,
  })));
}

function stableStorageDecoderSignature(field: ScanOcrFieldResult): string {
  const decoder = field.storageAddressDecoder;
  if (!decoder) return "null";
  return JSON.stringify({
    attempted: decoder.attempted,
    selected: decoder.selected,
    valid: decoder.valid,
    candidate: decoder.candidate,
    meanConfidence: decoder.meanConfidence,
    reason: decoder.reason,
    cells: decoder.cells.map((cell) => ({
      name: cell.name,
      rawText: cell.rawText,
      decodedText: cell.decodedText,
      confidence: cell.confidence,
      valid: cell.valid,
      correctionsApplied: cell.correctionsApplied,
      inputSha256: cell.inputEvidence?.sha256 ?? null,
    })),
  });
}

function parallelOverlapMetrics(tasks: Array<{ startedOffsetMs: number; finishedOffsetMs: number }>): {
  activeWorkersPeak: number;
  overlapMs: number;
  overlapRatio: number | null;
} {
  if (tasks.length === 0) return { activeWorkersPeak: 0, overlapMs: 0, overlapRatio: null };
  const events = tasks.flatMap((task) => ([
    { at: task.startedOffsetMs, delta: 1 },
    { at: task.finishedOffsetMs, delta: -1 },
  ])).sort((a, b) => a.at - b.at || a.delta - b.delta);
  let active = 0;
  let peak = 0;
  let overlapMs = 0;
  let previousAt = events[0]?.at ?? 0;
  for (const event of events) {
    if (event.at > previousAt && active >= 2) overlapMs += event.at - previousAt;
    active += event.delta;
    peak = Math.max(peak, active);
    previousAt = event.at;
  }
  const earliest = Math.min(...tasks.map((task) => task.startedOffsetMs));
  const latest = Math.max(...tasks.map((task) => task.finishedOffsetMs));
  const span = Math.max(0, latest - earliest);
  return {
    activeWorkersPeak: peak,
    overlapMs: roundShadowMetric(overlapMs, 1) ?? 0,
    overlapRatio: span > 0 ? roundShadowMetric(overlapMs / span, 4) : null,
  };
}

let pv2IosPoolBootGeneration = 0;

async function createParallelShadowWorker(slot: 1 | 2): Promise<TesseractWorker> {
  if (!isIosWebKitOcrRuntime()) {
    return createWorker(
      OCR_LANGUAGES,
      undefined,
      {
        workerPath: TESSERACT_WORKER_PATH,
        corePath: TESSERACT_CORE_PATH,
        langPath: TESSERACT_LANG_PATH,
        workerBlobURL: false,
        // Shadow progress must never hijack Production's global OCR progress UI.
        logger: () => undefined,
      }
    );
  }

  // v0.287.1: Der produktive iOS-OCR-Pfad hat bereits bewiesen, dass Safari den
  // klassischen Tesseract createWorker()->traineddata-fetch nicht zuverlässig
  // bootet. Der PV-2/PV-9-Pool muss deshalb exakt denselben managed Worker-
  // Transport mit den bereits im Main Thread verifizierten .bin/GZIP-Bytes
  // verwenden. Ein Pool-Worker darf die Diagnose des primären OCR-Workers nicht
  // überschreiben.
  if (!workerReady) {
    throw new Error("iOS Parallel-OCR-Pool darf erst nach READY des primären OCR-Workers starten.");
  }
  const languageAssets = workerIosLanguageAssets;
  if (!languageAssets || languageAssets.length !== IOS_TRAINEDDATA_ASSETS.length) {
    throw new Error("Verifizierte iOS-OCR-Sprachdaten fehlen für den Parallel-OCR-Pool.");
  }

  const generation = ++pv2IosPoolBootGeneration;
  const plan = createOcrBootPlan();
  let lastFailure: unknown = new Error(`iOS Parallel-OCR-Pool Worker ${slot} konnte nicht initialisiert werden.`);

  for (let index = 0; index < plan.length; index += 1) {
    const entry = plan[index];
    const attempt = index + 1;
    const startedMs = performance.now();
    const attemptDiagnostic: OcrBootAttemptDiagnostic = {
      attempt,
      coreMode: entry.mode,
      corePath: entry.corePath,
      startedAtUtc: new Date().toISOString(),
      finishedAtUtc: null,
      durationMs: null,
      outcome: "starting",
      lastLoggerStatus: null,
      workerError: null,
    };

    try {
      const created = await createManagedIosTesseractWorker(entry.corePath, attemptDiagnostic, {
        workerId: `ManagedIOSParallelWorker-${generation}-${slot}-${attempt}`,
        languageAssets,
        updatePrimaryDiagnostics: false,
      });
      attemptDiagnostic.outcome = "success";
      attemptDiagnostic.finishedAtUtc = new Date().toISOString();
      attemptDiagnostic.durationMs = Math.max(0, performance.now() - startedMs);
      return created;
    } catch (error: unknown) {
      lastFailure = error;
      attemptDiagnostic.finishedAtUtc = new Date().toISOString();
      attemptDiagnostic.durationMs = Math.max(0, performance.now() - startedMs);
      if (attemptDiagnostic.outcome !== "worker_error_event") attemptDiagnostic.outcome = "failed";

      // Genau wie beim primären iOS-Worker darf nur ein echter Worker-ErrorEvent
      // die konservativere Core-Variante aktivieren. Andere Fehler bleiben
      // fail-closed und werden nicht durch Core-Roulette verschleiert.
      if (attemptDiagnostic.outcome === "worker_error_event" && index + 1 < plan.length) continue;
      break;
    }
  }

  throw lastFailure instanceof Error
    ? lastFailure
    : new Error(String(lastFailure));
}

function clearPv2ShadowPoolIdleTimer(): void {
  if (pv2ShadowWorkerPoolIdleTimer !== null) {
    window.clearTimeout(pv2ShadowWorkerPoolIdleTimer);
    pv2ShadowWorkerPoolIdleTimer = null;
  }
}

export function terminateLabelOcrParallelShadowRuntime(): void {
  clearPv2ShadowPoolIdleTimer();
  const active = pv2ShadowWorkerPoolPromise;
  pv2ShadowWorkerPoolPromise = null;
  pv2ShadowWorkerPoolReady = false;
  pv2ShadowWorkerPoolUseCount = 0;
  if (!active) return;
  void active.then(async (workers) => {
    await Promise.allSettled(workers.map((worker) => worker.terminate()));
  }).catch(() => undefined);
}

function armPv2ShadowPoolIdleTimer(): void {
  clearPv2ShadowPoolIdleTimer();
  pv2ShadowWorkerPoolIdleTimer = window.setTimeout(() => {
    pv2ShadowWorkerPoolIdleTimer = null;
    terminateLabelOcrParallelShadowRuntime();
  }, PV2_SHADOW_POOL_IDLE_MS);
}

async function ensurePv2ShadowWorkerPool(): Promise<TesseractWorker[]> {
  if (!pv2ShadowWorkerPoolPromise) {
    const created = isIosWebKitOcrRuntime()
      ? (async (): Promise<TesseractWorker[]> => {
          // Safari: Worker-Boot strikt seriell. Erst wenn Worker 1 vollständig READY
          // ist, darf Worker 2 initialisieren. Parallel arbeiten dürfen beide erst
          // danach während der Recognition. So existieren nie zwei gleichzeitig
          // startende/terminierende Tesseract-WASM-Boots.
          const first = await createParallelShadowWorker(1);
          try {
            const second = await createParallelShadowWorker(2);
            return [first, second];
          } catch (error: unknown) {
            await first.terminate().catch(() => undefined);
            throw error;
          }
        })()
      : Promise.all([createParallelShadowWorker(1), createParallelShadowWorker(2)]);
    pv2ShadowWorkerPoolPromise = created;
    void created.then(() => {
      if (pv2ShadowWorkerPoolPromise === created) {
        pv2ShadowWorkerPoolReady = true;
        recordParallelRuntimeLifecycle("ocr", "pool_ready");
        armPv2ShadowPoolIdleTimer();
      }
    }).catch(() => {
      if (pv2ShadowWorkerPoolPromise === created) {
        pv2ShadowWorkerPoolPromise = null;
        pv2ShadowWorkerPoolReady = false;
      }
    });
  }
  const workers = await pv2ShadowWorkerPoolPromise;
  if (workers.length !== 2) throw new Error(`PV-4 OCR-Warmpool unvollständig: ${workers.length}/2 Worker.`);
  return workers;
}

export async function warmLabelOcrParallelShadowRuntime(): Promise<void> {
  await ensurePv2ShadowWorkerPool();
}

export async function exerciseLabelOcrParallelShadowRecovery(): Promise<void> {
  await ensurePv2ShadowWorkerPool();
  terminateLabelOcrParallelShadowRuntime();
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
  await ensurePv2ShadowWorkerPool();
}

async function recognizeLabelMaterialNumberWithShadowWorker(
  worker: TesseractWorker,
  file: File,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">,
): Promise<LabelOcrMaterialNumberProbe> {
  const startedMs = performance.now();
  const effectiveRuntime = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
  const configuredRegions = effectiveRuntime.ocr.regions as readonly NormalizedOcrRegion[];
  const parameters = effectiveRuntime.ocr.params ?? {};
  const image = await loadImage(file);
  const canvas = prepareCanvas(image, parameters.OCR_CONTRAST_FACTOR ?? 1.18);
  assertOcrImagePlausible(
    canvas,
    parameters.OCR_MIN_MEAN ?? 18,
    parameters.OCR_MAX_MEAN ?? 245,
    parameters.OCR_MIN_STDDEV ?? 10,
  );
  const smartRuntime = effectiveRuntime.ocr.smartGridMultiPass ?? false;
  const anchored = smartRuntime
    ? gridAnchorOcrRegions(canvas, configuredRegions)
    : { regions: configuredRegions.map((candidate) => ({ ...candidate })), details: null };
  const region = anchored.regions.find((candidate) => candidate.field === "materialNumber")
    ?? OCR_REGIONS.find((candidate) => candidate.field === "materialNumber");
  if (!region) throw new Error("Materialnummer-ROI ist nicht konfiguriert.");
  const field = smartRuntime
    ? await recognizeRegionSmart(
        worker,
        canvas,
        region,
        parameters,
        effectiveRuntime.ocr.canonicalPreprocess ?? false,
        anchored.details?.applied ? "grid-anchored" : "configured",
      )
    : await recognizeRegionOnce(
        worker,
        canvas,
        region,
        parameters,
        effectiveRuntime.ocr.canonicalPreprocess ?? false,
      );
  return {
    text: field.text,
    rawText: field.rawText,
    confidence: field.confidence,
    durationMs: Math.round(performance.now() - startedMs),
  };
}

export interface ParallelMaterialNumberCandidateProbe {
  candidateId: string;
  workerId: string;
  text: string;
  rawText: string;
  confidence: number | null;
  durationMs: number;
}

export async function runMaterialNumberParallelShadowBatch(
  candidates: Array<{ candidateId: string; file: File }>,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">,
): Promise<{ wallMs: number; workerPoolReused: boolean; workerStartupMs: number; probes: ParallelMaterialNumberCandidateProbe[] }> {
  if (candidates.length < 1 || candidates.length > 2) {
    throw new Error(`PV-6 Rerank-OCR erwartet 1–2 Kandidaten, erhalten: ${candidates.length}.`);
  }
  const workerPoolReused = pv2ShadowWorkerPoolReady;
  const startupStarted = performance.now();
  const workers = await ensurePv2ShadowWorkerPool();
  const workerStartupMs = workerPoolReused ? 0 : Math.round(performance.now() - startupStarted);
  pv2ShadowWorkerPoolUseCount += 1;
  armPv2ShadowPoolIdleTimer();
  const started = performance.now();
  const probes = await Promise.all(candidates.map(async (candidate, index) => {
    const probe = await recognizeLabelMaterialNumberWithShadowWorker(workers[index], candidate.file, runtimeSnapshot);
    return {
      candidateId: candidate.candidateId,
      workerId: `pv2-ocr-worker-${index + 1}`,
      ...probe,
    } satisfies ParallelMaterialNumberCandidateProbe;
  }));
  return {
    wallMs: Math.round(performance.now() - started),
    workerPoolReused,
    workerStartupMs,
    probes,
  };
}

export interface ParallelProductionFinalOcrExecution {
  result: LabelOcrResult;
  requestedWorkers: 2;
  effectiveWorkers: number;
  activeWorkersPeak: number;
  workerStartupMs: 0;
  parallelWallMs: number;
  overlapMs: number;
  overlapRatio: number | null;
  mergeOrder: ScanOcrFieldResult["field"][];
  tasks: ScanOcrParallelShadowTask[];
}

/**
 * PV-9 Production Final-OCR cutover.
 *
 * This function is intentionally warm-only. It never creates a worker inside the
 * Shutter→Step-2 critical path. Admission/soak gating is evaluated by the caller.
 * The implementation is the same deterministic 2+2 field split that PV-2 has
 * already validated against the serial Production path.
 */
export async function recognizeLabelDataParallelProduction(
  file: File,
  onProgress?: (progress: number) => void,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">,
  options: { recognitionRunId?: string | null } = {},
): Promise<ParallelProductionFinalOcrExecution> {
  if (!pv2ShadowWorkerPoolReady || !pv2ShadowWorkerPoolPromise) {
    throw new Error("PV-9 OCR-Parallelpool ist nicht vorgewärmt; serieller Fallback erforderlich.");
  }

  const workers = await pv2ShadowWorkerPoolPromise;
  if (workers.length !== 2) {
    throw new Error(`PV-9 OCR-Parallelpool unvollständig: ${workers.length}/2 Worker.`);
  }

  pv2ShadowWorkerPoolUseCount += 1;
  armPv2ShadowPoolIdleTimer();

  const startedAt = new Date();
  const startedMs = performance.now();
  try {
    const effectiveRuntime = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
    const configuredRegions = effectiveRuntime.ocr.regions as readonly NormalizedOcrRegion[];
    const parameters = effectiveRuntime.ocr.params ?? {};
    const canonicalOcrRuntime = effectiveRuntime.ocr.canonicalPreprocess ?? false;
    const smartOcrRuntime = effectiveRuntime.ocr.smartGridMultiPass ?? false;
    const image = await loadImage(file);
    const canvas = prepareCanvas(image, parameters.OCR_CONTRAST_FACTOR ?? 1.18);
    assertOcrImagePlausible(
      canvas,
      parameters.OCR_MIN_MEAN ?? 18,
      parameters.OCR_MAX_MEAN ?? 245,
      parameters.OCR_MIN_STDDEV ?? 10,
    );

    const gridPlan = smartOcrRuntime
      ? gridAnchorOcrRegions(canvas, configuredRegions)
      : {
          regions: configuredRegions.map((region) => ({ ...region })),
          details: {
            applied: false,
            horizontalMatches: 0,
            verticalMatches: 0,
            horizontalScale: null,
            horizontalOffset: null,
            verticalScale: null,
            verticalOffset: null,
            meanResidual: null,
            reason: "Production-Runtime verwendet konfigurierte ROIs ohne Smart-Grid-Anker.",
          } satisfies ScanOcrGridAnchoringDetails,
        };

    const priority: Record<ScanOcrFieldResult["field"], number> = {
      materialNumber: 0,
      storageAddress: 1,
      materialType: 2,
      materialText: 3,
    };
    const regions = [...gridPlan.regions].sort((a, b) => priority[a.field] - priority[b.field]);
    const storageSubcells = smartOcrRuntime ? storageSubcellOcrRegions(gridPlan.details) : [];
    const assignments: NormalizedOcrRegion[][] = [
      regions.filter((_, index) => index % 2 === 0),
      regions.filter((_, index) => index % 2 === 1),
    ];

    const taskOriginMs = performance.now();
    const tasks: ScanOcrParallelShadowTask[] = [];
    const parallelFields: ScanOcrFieldResult[] = [];
    let completedFields = 0;

    await Promise.all(assignments.map(async (assigned, workerIndex) => {
      const worker = workers[workerIndex];
      const workerId = `pv9-ocr-worker-${workerIndex + 1}`;
      for (const region of assigned) {
        const taskStartedMs = performance.now();
        const field = smartOcrRuntime
          ? await recognizeRegionSmart(
              worker,
              canvas,
              region,
              parameters,
              canonicalOcrRuntime,
              gridPlan.details.applied ? "grid-anchored" : "configured",
              region.field === "storageAddress" ? storageSubcells : [],
            )
          : {
              ...await recognizeRegionOnce(worker, canvas, region, parameters, canonicalOcrRuntime),
              roiSource: "configured" as const,
              selectedPass: "base",
              passCount: 1,
            };
        const taskFinishedMs = performance.now();
        parallelFields.push(field);
        tasks.push({
          executionId: `${options.recognitionRunId ?? "no-run"}:pv9:${workerIndex + 1}:${region.field}`,
          workerId,
          field: region.field,
          executionContext: "webworker",
          transferMode: "shared-canvas-read",
          startedOffsetMs: roundShadowMetric(taskStartedMs - taskOriginMs, 1) ?? 0,
          finishedOffsetMs: roundShadowMetric(taskFinishedMs - taskOriginMs, 1) ?? 0,
          durationMs: Math.round(taskFinishedMs - taskStartedMs),
        });
        completedFields += 1;
        onProgress?.(Math.min(0.99, completedFields / regions.length));
      }
    }));

    parallelFields.sort((a, b) => priority[a.field] - priority[b.field]);
    tasks.sort((a, b) => priority[a.field] - priority[b.field]);

    const byField = new Map(parallelFields.map((field) => [field.field, field.text]));
    const finishedAt = new Date();
    const parallelWallMs = Math.round(performance.now() - startedMs);
    const overlap = parallelOverlapMetrics(tasks);

    const result: LabelOcrResult = {
      data: {
        materialText: byField.get("materialText") ?? "",
        materialType: byField.get("materialType") ?? "",
        materialNumber: byField.get("materialNumber") ?? "",
        orderInfo: "",
        storageAddress: byField.get("storageAddress") ?? "",
      },
      details: {
        engine: "tesseract.js",
        languages: OCR_LANGUAGES,
        status: "done",
        startedAt: startedAt.toISOString(),
        finishedAt: finishedAt.toISOString(),
        durationMs: parallelWallMs,
        progress: 1,
        fields: parallelFields,
        gridAnchoring: gridPlan.details,
        masterResolution: null,
        parallelShadow: null,
        parallelProduction: null,
        error: null,
      },
    };

    onProgress?.(1);
    return {
      result,
      requestedWorkers: 2,
      effectiveWorkers: overlap.activeWorkersPeak >= 2 ? 2 : 1,
      activeWorkersPeak: overlap.activeWorkersPeak,
      workerStartupMs: 0,
      parallelWallMs,
      overlapMs: overlap.overlapMs,
      overlapRatio: overlap.overlapRatio,
      mergeOrder: regions.map((region) => region.field),
      tasks,
    };
  } catch (error: unknown) {
    recordParallelRuntimeLifecycle("ocr", "runtime_error_recycle");
    terminateLabelOcrParallelShadowRuntime();
    throw error;
  } finally {
    if (pv2ShadowWorkerPoolReady) armPv2ShadowPoolIdleTimer();
  }
}



if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    recordParallelRuntimeLifecycle("ocr", "pagehide_terminated");
    terminateLabelOcrParallelShadowRuntime();
  }, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    recordParallelRuntimeLifecycle("ocr", "visibility_hidden_terminated");
    terminateLabelOcrParallelShadowRuntime();
  }, { passive: true });
}

export async function runLabelOcrParallelShadowCandidate(
  file: File,
  serialResult: LabelOcrResult,
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">,
  options: ParallelShadowCandidateOptions = {},
): Promise<Record<string, unknown>> {
  const startedAtUtc = new Date().toISOString();
  const startupStartedMs = performance.now();
  let workers: TesseractWorker[] = [];
  let createdWorkers = 0;
  let rectifiedFileSha256: string | null = null;
  let outcome: Record<string, unknown> | null = null;
  const workerPoolReused = pv2ShadowWorkerPoolReady;
  try {
    workers = await ensurePv2ShadowWorkerPool();
    createdWorkers = workers.length;
    const workerStartupMs = workerPoolReused ? 0 : Math.round(performance.now() - startupStartedMs);
    pv2ShadowWorkerPoolUseCount += 1;
    armPv2ShadowPoolIdleTimer();
    const candidateStartedMs = performance.now();
    rectifiedFileSha256 = await sha256BlobForShadow(file);

    const effectiveRuntime = runtimeSnapshot ?? await createVisionRuntimeSnapshot();
    const configuredRegions = effectiveRuntime.ocr.regions as readonly NormalizedOcrRegion[];
    const parameters = effectiveRuntime.ocr.params ?? {};
    const canonicalOcrRuntime = effectiveRuntime.ocr.canonicalPreprocess ?? false;
    const smartOcrRuntime = effectiveRuntime.ocr.smartGridMultiPass ?? false;
    const image = await loadImage(file);
    const canvas = prepareCanvas(image, parameters.OCR_CONTRAST_FACTOR ?? 1.18);
    assertOcrImagePlausible(
      canvas,
      parameters.OCR_MIN_MEAN ?? 18,
      parameters.OCR_MAX_MEAN ?? 245,
      parameters.OCR_MIN_STDDEV ?? 10,
    );
    const gridPlan = smartOcrRuntime
      ? gridAnchorOcrRegions(canvas, configuredRegions)
      : {
          regions: configuredRegions.map((region) => ({ ...region })),
          details: {
            applied: false,
            horizontalMatches: 0,
            verticalMatches: 0,
            horizontalScale: null,
            horizontalOffset: null,
            verticalScale: null,
            verticalOffset: null,
            meanResidual: null,
            reason: "Production-Runtime verwendet konfigurierte ROIs ohne Smart-Grid-Anker.",
          } satisfies ScanOcrGridAnchoringDetails,
        };
    const priority: Record<ScanOcrFieldResult["field"], number> = {
      materialNumber: 0,
      storageAddress: 1,
      materialType: 2,
      materialText: 3,
    };
    const regions = [...gridPlan.regions].sort((a, b) => priority[a.field] - priority[b.field]);
    const storageSubcells = smartOcrRuntime ? storageSubcellOcrRegions(gridPlan.details) : [];
    const assignments: NormalizedOcrRegion[][] = [
      regions.filter((_, index) => index % 2 === 0),
      regions.filter((_, index) => index % 2 === 1),
    ];
    const taskOriginMs = performance.now();
    const tasks: Array<{
      executionId: string;
      workerId: string;
      field: ScanOcrFieldResult["field"];
      executionContext: "webworker";
      transferMode: "shared-canvas-read";
      startedOffsetMs: number;
      finishedOffsetMs: number;
      durationMs: number;
    }> = [];
    const parallelFields: ScanOcrFieldResult[] = [];

    await Promise.all(assignments.map(async (assigned, workerIndex) => {
      const worker = workers[workerIndex];
      const workerId = `pv2-ocr-worker-${workerIndex + 1}`;
      for (const region of assigned) {
        const taskStartedMs = performance.now();
        const field = smartOcrRuntime
          ? await recognizeRegionSmart(
              worker,
              canvas,
              region,
              parameters,
              canonicalOcrRuntime,
              gridPlan.details.applied ? "grid-anchored" : "configured",
              region.field === "storageAddress" ? storageSubcells : [],
            )
          : {
              ...await recognizeRegionOnce(worker, canvas, region, parameters, canonicalOcrRuntime),
              roiSource: "configured" as const,
              selectedPass: "base",
              passCount: 1,
            };
        const taskFinishedMs = performance.now();
        parallelFields.push(field);
        tasks.push({
          executionId: `${options.recognitionRunId ?? "no-run"}:pv2:${workerIndex + 1}:${region.field}`,
          workerId,
          field: region.field,
          executionContext: "webworker",
          // Tesseract.js reads the same immutable canvas source; no recognition
          // bitmap is transferred between PV-2 workers by completion order.
          transferMode: "shared-canvas-read",
          startedOffsetMs: roundShadowMetric(taskStartedMs - taskOriginMs, 1) ?? 0,
          finishedOffsetMs: roundShadowMetric(taskFinishedMs - taskOriginMs, 1) ?? 0,
          durationMs: Math.round(taskFinishedMs - taskStartedMs),
        });
      }
    }));

    parallelFields.sort((a, b) => priority[a.field] - priority[b.field]);
    tasks.sort((a, b) => priority[a.field] - priority[b.field]);
    const byField = new Map(parallelFields.map((field) => [field.field, field.text]));
    const parallelData: RecognizedLabelData = {
      materialText: byField.get("materialText") ?? "",
      materialType: byField.get("materialType") ?? "",
      materialNumber: byField.get("materialNumber") ?? "",
      orderInfo: "",
      storageAddress: byField.get("storageAddress") ?? "",
    };
    const parallelWallMs = Math.round(performance.now() - candidateStartedMs);
    const serialFields = new Map(serialResult.details.fields.map((field) => [field.field, field]));
    const parallelFieldMap = new Map(parallelFields.map((field) => [field.field, field]));
    const fieldParity = regions.map((region) => {
      const serial = serialFields.get(region.field);
      const parallel = parallelFieldMap.get(region.field);
      return {
        field: region.field,
        textMatch: Boolean(serial && parallel && serial.text === parallel.text),
        confidenceMatch: Boolean(serial && parallel && confidenceEqual(serial.confidence, parallel.confidence)),
        selectedPassMatch: Boolean(serial && parallel && (serial.selectedPass ?? "base") === (parallel.selectedPass ?? "base")),
        passSequenceMatch: Boolean(serial && parallel && stableShadowPassSignature(serial) === stableShadowPassSignature(parallel)),
        roiShaMatch: Boolean(serial && parallel && (serial.inputEvidence?.sha256 ?? null) === (parallel.inputEvidence?.sha256 ?? null)),
        storageDecoderMatch: Boolean(serial && parallel && stableStorageDecoderSignature(serial) === stableStorageDecoderSignature(parallel)),
        serialText: serial?.text ?? "",
        parallelText: parallel?.text ?? "",
        serialConfidence: serial?.confidence ?? null,
        parallelConfidence: parallel?.confidence ?? null,
        serialSelectedPass: serial?.selectedPass ?? null,
        parallelSelectedPass: parallel?.selectedPass ?? null,
        serialRoiSha256: serial?.inputEvidence?.sha256 ?? null,
        parallelRoiSha256: parallel?.inputEvidence?.sha256 ?? null,
      };
    });
    const finalFields = ["materialText", "materialType", "materialNumber", "storageAddress"] as const;
    const finalFourFieldsMatch = finalFields.every((field) => serialResult.data[field] === parallelData[field]);
    const gridAnchoringMatch = JSON.stringify(serialResult.details.gridAnchoring ?? null) === JSON.stringify(gridPlan.details ?? null);
    const allRoiShaMatch = fieldParity.every((field) => field.roiShaMatch);
    const allTextMatch = fieldParity.every((field) => field.textMatch);
    const allConfidenceMatch = fieldParity.every((field) => field.confidenceMatch);
    const allPassSelectionMatch = fieldParity.every((field) => field.selectedPassMatch && field.passSequenceMatch && field.storageDecoderMatch);
    const recognitionParityPass = gridAnchoringMatch
      && allRoiShaMatch
      && allTextMatch
      && allConfidenceMatch
      && allPassSelectionMatch
      && finalFourFieldsMatch;
    const overlap = parallelOverlapMetrics(tasks);
    const serialWallMs = serialResult.details.durationMs ?? null;

    outcome = {
      schemaVersion: 1,
      contractVersion: "same-raw-serial-parallel-shadow-v1",
      profileId: "ocr-2-worker-2plus2-shadow-v1",
      status: recognitionParityPass ? "pass" : "mismatch",
      recognitionRunId: options.recognitionRunId ?? null,
      startedAtUtc,
      finishedAtUtc: new Date().toISOString(),
      productionInfluenced: false,
      productionExecutionProfile: "serial-production-v1",
      upstreamMode: "shared-serial-detection-rectification",
      rectifiedFileSha256,
      requestedWorkers: 2,
      createdWorkers,
      effectiveWorkers: overlap.activeWorkersPeak >= 2 ? 2 : 1,
      activeWorkersPeak: overlap.activeWorkersPeak,
      workersTerminated: false,
      workersRetainedForReuse: true,
      workerPoolWarm: true,
      workerPoolReused,
      workerPoolReuseCount: pv2ShadowWorkerPoolUseCount,
      mergeOrder: regions.map((region) => region.field),
      workerStartupMs,
      serialWallMs,
      parallelWallMs,
      estimatedSpeedup: serialWallMs && parallelWallMs > 0 ? roundShadowMetric(serialWallMs / parallelWallMs, 3) : null,
      overlapMs: overlap.overlapMs,
      overlapRatio: overlap.overlapRatio,
      tasks,
      fieldParity,
      parity: {
        sameFileObject: true,
        sharedDetectionGeometry: true,
        sharedRectification: true,
        gridAnchoringMatch,
        allRoiShaMatch,
        allTextMatch,
        allConfidenceMatch,
        allPassSelectionMatch,
        finalFourFieldsMatch,
        recognitionParityPass,
      },
      fallbackReason: recognitionParityPass ? null : "RECOGNITION_PARITY_MISMATCH",
      error: null,
    };
  } catch (error: unknown) {
    outcome = {
      schemaVersion: 1,
      contractVersion: "same-raw-serial-parallel-shadow-v1",
      profileId: "ocr-2-worker-2plus2-shadow-v1",
      status: "error",
      recognitionRunId: options.recognitionRunId ?? null,
      startedAtUtc,
      finishedAtUtc: new Date().toISOString(),
      productionInfluenced: false,
      productionExecutionProfile: "serial-production-v1",
      upstreamMode: "shared-serial-detection-rectification",
      rectifiedFileSha256,
      requestedWorkers: 2,
      createdWorkers,
      effectiveWorkers: 0,
      activeWorkersPeak: 0,
      workersTerminated: true,
      workersRetainedForReuse: false,
      workerPoolWarm: false,
      workerPoolReused,
      workerPoolReuseCount: pv2ShadowWorkerPoolUseCount,
      mergeOrder: ["materialNumber", "storageAddress", "materialType", "materialText"],
      workerStartupMs: null,
      serialWallMs: serialResult.details.durationMs ?? null,
      parallelWallMs: null,
      estimatedSpeedup: null,
      overlapMs: null,
      overlapRatio: null,
      tasks: [],
      fieldParity: [],
      parity: {
        sameFileObject: true,
        sharedDetectionGeometry: true,
        sharedRectification: true,
        gridAnchoringMatch: null,
        allRoiShaMatch: null,
        allTextMatch: null,
        allConfidenceMatch: null,
        allPassSelectionMatch: null,
        finalFourFieldsMatch: null,
        recognitionParityPass: null,
      },
      fallbackReason: "SHADOW_RUNTIME_ERROR",
      error: error instanceof Error ? error.message : String(error),
    };
  } finally {
    const failed = outcome?.status === "error";
    if (failed) {
      recordParallelRuntimeLifecycle("ocr", "runtime_error_recycle");
      terminateLabelOcrParallelShadowRuntime();
      if (outcome) {
        outcome.workersTerminated = true;
        outcome.workersRetainedForReuse = false;
        outcome.workerPoolWarm = false;
      }
    } else {
      armPv2ShadowPoolIdleTimer();
    }
  }
  if (!outcome) throw new Error("PV-2 Shadow lieferte kein Evidence-Ergebnis.");
  return outcome;
}

const PV2_SHADOW_END = "PV2_SAME_RAW_PARALLEL_SHADOW_END" as const;
void PV2_SHADOW_END;
